// Bare shared-library import, not per-file deep functions/components subpaths
// — a per-file deep specifier into @symbo-ls/workspace-shared cannot resolve
// through the platform's public importmap (tickets/execution-board.md §1,
// same fix as workspace/app.js). Deferred call/getter wrappers, not a
// module-scope destructure, per the a5d0a2aa barrel-cycle lesson.
import _wsShared from '@symbo-ls/workspace-shared'
import { touchFloor } from '@symbo-ls/workspace-shared/components/_touchTarget.js'
const installKeyboardAvoidance = (...args) => _wsShared.functions.installKeyboardAvoidance(...args)
const getHeaderIslandMinHeight = () => _wsShared.components.HEADER_ISLAND_MIN_HEIGHT
// A touch screen — the shell's registered capability switch (shared/functions/
// pointerCapability.js `isCoarsePointer`: the coarse primary pointer, or touch
// points). False in a host that registers none (the composer then keeps the
// desktop pill). A JS read, not an '@coarse' block, on purpose: the pill's
// geometry lives in the reactive `.isSmall` / size props, which the live shell
// applies inline, and a nested media block there did not reliably re-attach on
// a size flip (DESIGN-SHELL-RESPONSIVE-IOS-SAFARI-1).
const _touchScreen = (el) => {
  const fn = el && el.context && el.context.functions && el.context.functions.isCoarsePointer
  return typeof fn === 'function' && !!fn()
}
// Content-reference mention machinery (shared/functions/mentionRefs.js —
// the AI-composer mirror of chat's trigger grammar; see its header).
const _detectMentionRefTrigger = (...a) => _wsShared.functions.detectMentionRefTrigger(...a)
const _mentionRefSyncOptions = (...a) => _wsShared.functions.mentionRefSyncOptions(...a)
const _fetchMentionRefOptions = (...a) => _wsShared.functions.fetchMentionRefOptions(...a)
const _mentionRefRowLabel = (...a) => _wsShared.functions.mentionRefRowLabel(...a)
// THE house keyboard model for menus (shared/functions/menuKeyboard.js — the
// same helpers the AI model switcher, the AI settings panel and the assistant
// Sessions menu use). Deferred wrappers, never a module-scope destructure,
// per the a5d0a2aa barrel-cycle lesson above.
const _menuArrowNav = (...a) => _wsShared.functions.menuArrowNav(...a)
const _menuActivateRow = (...a) => _wsShared.functions.menuActivateRow(...a)
const _armNamedMenuFocus = (...a) => _wsShared.functions.armNamedMenuFocus(...a)
const _releaseMenuFocus = (...a) => _wsShared.functions.releaseMenuFocus(...a)
const _restoreMenuFocus = (...a) => _wsShared.functions.restoreMenuFocus(...a)
// THE pre-send key model of this composer — the attach-menu arrow branch and
// the whole mention-picker traversal (shared/functions/aiComposerKeys.js).
// It lives in shared, not here, because the SHELL mounts this component and
// REPLACES `Textarea.onKeydown` at the mount site (workspace/app.js): both
// mounts delegate to this one function, so a branch can never exist on one
// mount and be missing on the other.
const _aiComposerKeydown = (...a) => _wsShared.functions.aiComposerKeydown(...a)
const _applyAiMention = (...a) => _wsShared.functions.applyAiMentionPick(...a)

// ── the '+' attach menu's focus lifecycle lives at its CALL SITES ─────────
// (2026-09-02, after the first cut of this wiring shipped dead.)
//
// `AttachMenu` is `if:`-gated, and smbls fires NO lifecycle hook on an
// if-gated element's open or close edge:
//   • an element whose `if:` is false at creation returns from `create()`
//     inside the `if (!ref.__if)` branch — BEFORE the
//     `triggerLifecycle('Render' | 'Create' | 'Complete')` calls further down
//     (smbls/packages/element/src/create.js);
//   • `registerIfEffect`'s first-activation branch rebuilds the node and
//     queues `onMounted`, but never Render/Create/Complete, and its
//     re-activation branch only re-attaches the existing node — no hook at all;
//   • the close edge is a bare `element.node.remove()` with no `dispose()`,
//     and `onRemove` only ever fires from `dispose()`.
// So `onRender: focusOnOpen` / `onRemove: focusBack` on this panel were both
// unreachable: the menu opened correctly and focus never entered it, at every
// open including the first. (Filed for the framework separately — the sibling
// menus in this pass are all always-mounted panels whose `onRender` +
// `subscribeTo`/`onUpdate` hooks DO fire, so this panel was the only casualty.)
//
// The fix is not a retry: whoever flips `attachOpen` IS the edge, so it arms
// the panel's focus directly. The reach is `armNamedMenuFocus` (shared) —
// `el.lookup('AttachMenu')` resolves the panel from anywhere in the composer,
// because `create()` publishes an if-hidden child on its parent under its own
// key and `lookup` checks each ancestor's named children. The SHELL's mount of
// this composer overrides `Textarea.onKeydown` wholesale
// (workspace/app.js CanvasPromptTextarea > Textarea), so it calls the same
// shared helper — one implementation, two call sites, no drift.
const _attachPanelOf = (el) =>
  el && typeof el.lookup === 'function' ? el.lookup('AttachMenu') : null

const _armAttachFocus = (el, opts) => _armNamedMenuFocus(el, 'AttachMenu', opts)

// ONE PANEL AT A TIME (finding 3 of the 2026-10-01 frames): the "+" menu is this composer's LOCAL `attachOpen`, but
// the AI focus panel (shared SimoneSuggestions) yields to it through the ROOT (`_promptMenuOpen`, read beside the
// dropdown layer's `_dropdownLayer` — design-d2 8673ce440). Every site that flips `attachOpen` goes through here, so
// the mirror can never drift from the menu; the composer's unmount clears a mirror left up.
const _attachUpdate = (s, patch) => {
  s.update(patch)
  const root = s && s.root
  const open = !!s.attachOpen
  if (root && typeof root.update === 'function' && !!root._promptMenuOpen !== open) {
    root.update({ _promptMenuOpen: open }, { preventFetch: true })
  }
}

// Human-readable file size for a dropped-file chip's tooltip + the context
// line _sendPrompt appends to the prompt body. No registered `formatBytes`
// reaches this project (chat's usage of it resolves through the WORKSPACE
// project's own functions/formatUtils.js — chat is mounted inside the
// workspace shell's context, not a sibling package; canvas's own
// sharedLibraries.js doesn't list workspace), so this is a small local
// formatter rather than duplicating a cross-package dependency that doesn't
// actually reach here.
const _fmtBytes = (n) => {
  if (n == null || Number.isNaN(n)) return ''
  if (n < 1024) return `${n} B`
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`
  return `${(n / (1024 * 1024)).toFixed(1)} MB`
}

// Internal helper — read the textarea draft + any dropped/pasted
// files/refs/images from state and dispatch freestyleSend. Used by both the
// CircleButton onClick and the Textarea onKeydown Enter path. Reads/clears
// via state (no el.node.value access) to comply with FA511.
const _sendPrompt = (el) => {
  const st = el.state
  const draft = ((st && st.draft) || '').trim()
  const files = (st && st.files) || []
  const refs = (st && st.refs) || []
  // state.images (dropped/pasted/uploaded pictures) used to be read nowhere
  // in this function at all — they sat in the gallery, never reached
  // freestyleSend, and were never even cleared on send. Now read + included
  // like files/refs below (see the freestyleSend attachments param + its
  // header comment in freestyleBridge.js for exactly what does/doesn't
  // happen to them once sent).
  const images = (st && st.images) || []
  if (!draft && !files.length && !refs.length && !images.length) return

  // Build mode (root._aiBuildMode, the '+' menu toggle): the send runs the
  // addapp build pipeline instead of the freestyle answer path —
  // forceBuildApp sends headlessly and its watcher opens the generated
  // page as a PageSlot overlay when the output lands. Attachments stay in
  // the composer (the build pipeline carries text only today).
  const root = el.state && el.state.root
  if (root && root._aiBuildMode && draft) {
    try {
      el.call('forceBuildApp', draft)
      // the emptied draft re-measures through the framework (see _resetTextareaAutosize)
      if (st && st.update) st.update({ draft: '' })
      return
    } catch (_) {
      /* fall through to the normal send when no host registers the fn */
    }
  }

  // freestyleSend (freestyleBridge.js) ships `{type:'user_message', text,
  // context, attachments}` over the local-agent WebSocket. `attachments` is a
  // real structured field that DOES reach the bridge process — but the
  // receiving side doesn't forward it to the agent yet (framework-owned, see
  // freestyleSend's header comment). Until that lands, a readable context
  // line is the only thing guaranteed to reach the agent, so every
  // attachment ALSO gets one here, same treatment refs/files already get:
  // refs use the shared dragRefToText markdown-link format (the agent gets a
  // working deep link back to the ticket/project/page), files/images surface
  // name/type/size only — never raw bytes in the TEXT body (images' actual
  // bytes ride the separate `attachments` array below instead).
  const contextLines = [
    ...refs.map((ref) => el.call('dragRefToText', ref)),
    ...files.map((f) => `[Attached file: ${f.name} (${f.mime}, ${_fmtBytes(f.size)})]`),
    ...images.map((img) => `[Attached image: ${img.name} (${img.mime}, ${_fmtBytes(img.size)})]`)
  ]
  const body = contextLines.length ? [draft, ...contextLines].filter(Boolean).join('\n') : draft
  const attachments = images.map((img) => ({
    name: img.name,
    mime: img.mime,
    size: img.size,
    dataUrl: img.dataUrl
  }))

  // ONE send, ONE generation: the prompt goes to the agent and nowhere else
  // (Nika 2026-10-28 — the parallel blind page generator that a "create/build"
  // prompt used to start beside it is gone).
  el.call('freestyleSend', body, attachments)
  // No hand height clear: the framework re-measures the emptied draft on this
  // state write (set 10 — see _resetTextareaAutosize).
  if (st && st.update) st.update({ draft: '', files: [], refs: [], images: [] })
}

// Clears the autoGrow HEIGHT pin before a SIZE FLIP. The framework's
// `autoGrow` owns content growth in the small AND normal states (see the
// Textarea's `autoGrow`) and writes an inline px `height`. `s.size` flipping
// between small/normal/large (Expand/Minimize/fullscreen-toggle below) would
// leave the PREVIOUS state's px height on the node — an inline style always
// wins over the new state's floor, the "textarea autogrow vs [state] race" the
// voice-UX sweep this file landed with flagged by name (the settle's fit,
// below, then measures the new state frame by frame).
//
// NOT on a send (2026-10-02). A send empties the draft through state, and
// framework set 10 (smbls 3.14.818 / element 3.14.794) re-measures on that
// state write — measured live on dev (composer-heights matrix): an emptied
// draft settles at 39 px in the pill and at the E2 floor expanded, and a
// draft written through state grows to its content. The send's old hand clear
// was worse than redundant: landing after the framework's measure, it dropped
// the textarea to the brand Textarea's class height (E1, 153 px) — an empty
// pill four rows tall.
//
// ONLY `height`, NEVER `minHeight` (2026-09-29 per Nika "when we expand
// canvaschattextarea and start typing it shrinks the height"). The inline
// min-height IS the framework's reactive size floor (small auto / normal E2
// / large 75dvh), and its effect re-runs on a size change only: a clear here
// — a send from the expanded composer — left the box at one row under its
// own floor until the next size change. This component no longer writes
// a min-height anywhere, so there is no stale one to clear.
//
// Resolves the textarea NODE the same way the original send-time reset did
// (keydown passes the Textarea element itself; a toolbar button reaches it
// through the composer node) — walking the element graph for `.Textarea`
// silently no-ops here (verified live 2026-07-24 — the handler's `el`
// resolves children differently than the devtools element ref).
const _resetTextareaAutosize = (el) => {
  const node = el.node
  const taNode =
    (node && node.tagName === 'TEXTAREA' && node) ||
    node?.closest?.('[data-key="CanvasPromptTextarea"]')?.querySelector?.('textarea') ||
    el.parent?.node?.querySelector?.('textarea')
  if (taNode && taNode.style) taNode.style.height = ''
}

// The size-change settle (2026-09-02 per Nika "fix why after expand,
// minimize of promptTextarea does not get correct size"; THE FIT since
// 2026-09-29, design-d2's review of the expand/type fix). The pre-update
// _resetTextareaAutosize alone is sequenced wrong: the update then re-runs
// the `autoGrow` factory, whose framework resize writes a fresh
// `style.height = scrollHeight + 'px'` measured under the OLD cascade — the
// min-height floor and the paddings are still at their start values when a
// size flip begins, and they ease for the whole B step. That stale pixel
// height is inline, so it outranks the new state's floor.
//
// THE FIT, the same step on every frame from the click through 420 ms (the
// B morph and its settle), and once more by timer at 420 ms — a hidden tab
// runs no frames: collapse the height to `auto`, read the content
// (scrollHeight), and pin it ONLY when the content is taller than the box
// the easing floor gives it (clientHeight); otherwise leave `auto`, so the
// floor carries the box. A multi-line draft therefore never drops below its
// start or end height: Expand grows it with the easing paddings, Minimize
// shrinks it to the pill's content height, Exit fullscreen follows the floor
// down and stops on the content. Two older shapes, both measured, and why
// they are gone:
//   • a plain first-frame CLEAR + a 420 ms re-pin (2026-09-02 to 09-29):
//     with `height` out of the Textarea's transition list both steps are
//     instant, so a draft taller than one row dipped to one row and jumped
//     back at 420 ms (design-d2's model, E2 162 px, B 200 ms: Expand a
//     5-line pill 141 → 47 → 162 → 189; Minimize 10 lines 317 → 38.5 → 268;
//     Exit fullscreen 902 → 162 → 317);
//   • ALWAYS pinning scrollHeight mid-morph (before 2026-09-02): the read
//     was floored by the still-large box, so the pin froze a shrink.
// The fit writes `height` and nothing else — never the min-height floor (the
// framework's inline write, see the Textarea's `minHeight`), never 0 px (a
// node without layout reads 0, which is not taller than 0, so it keeps
// `auto`).
const _settleTextareaSize = (el) => {
  const taOf = () => {
    const node = el.node
    return (
      (node && node.tagName === 'TEXTAREA' && node) ||
      node?.closest?.('[data-key="CanvasPromptTextarea"]')?.querySelector?.('textarea') ||
      el.parent?.node?.querySelector?.('textarea')
    )
  }
  const fit = () => {
    const taNode = taOf()
    if (!taNode || !taNode.style) return
    taNode.style.height = 'auto'
    const h = taNode.scrollHeight
    if (h > taNode.clientHeight) taNode.style.height = h + 'px'
  }
  const until = Date.now() + 420
  const frame = () => {
    fit()
    if (Date.now() < until) _raf(frame)
  }
  _raf(frame)
  setTimeout(fit, 420)
}

// ── Mention typeahead driver (2026-09-02 per Nika "it could be also
// mentioned by @ ++ # and etc just like we have in chat") ─────────────────
// Runs on every input: detect a trigger at the caret (plain-text textarea —
// value + selectionStart), resolve options (person/channel synchronously
// from root state; ticket/event/reference via the shared SDK fetchers), and
// hold the picker on the COMPOSER's own state (`mentionPicker`) — never a
// root key, so the standalone canvas host takes no undeclared-root-key
// writes. The dropdown + keyboard nav read the same state.
const _refreshAiMention = (el, s) => {
  const node = el.node
  if (!node || typeof node.selectionStart !== 'number') return
  const trig = _detectMentionRefTrigger(node.value || '', node.selectionStart)
  if (!trig) {
    if (s.mentionPicker) s.update({ mentionPicker: null })
    return
  }
  const base = {
    type: trig.type,
    query: trig.query,
    start: trig.start,
    end: trig.end,
    activeIndex: 0
  }
  const sync = _mentionRefSyncOptions(trig.type, trig.query, s.root)
  if (sync) {
    s.update({ mentionPicker: { ...base, options: sync } })
    return
  }
  // Async types: open empty (position settles), fill when the fetch lands —
  // guarded against a stale response (type/query moved on).
  s.update({ mentionPicker: { ...base, options: [] } })
  _fetchMentionRefOptions(trig.type, trig.query)
    .then((opts) => {
      const cur = el.state && el.state.mentionPicker
      if (!cur || cur.type !== trig.type || cur.query !== trig.query) return
      el.state.update({ mentionPicker: { ...cur, options: opts } })
    })
    .catch(() => {})
}

// Applying a picked option (splice the insert text over the [start, end)
// trigger span, close the picker, restore the caret) is `applyAiMentionPick`
// in shared/functions/aiComposerKeys.js — aliased as `_applyAiMention` at the
// top of this file. It moved there with the key model it serves, so the shell
// mount's overridden keydown reaches the SAME implementation this file's
// dropdown rows use.

// rAF that survives a windowless runtime (bun test, bypass mode) — a bare
// requestAnimationFrame reference THROWS there, which turned a picker
// Enter into an aborted handler in the suite. Falls to a 16ms tick.
const _raf = (fn) =>
  typeof requestAnimationFrame === 'function' ? requestAnimationFrame(fn) : setTimeout(fn, 16)

// T18 — is THIS composer the live voice host? True while voice is open and
// root.voiceHost names the canvas prompt (the VoiceBtn below writes it before
// voicePttPress — full contract in shared/components/VoiceStrip.js; the
// voiceMinimized axis is deliberately ignored while a composer hosts). Every
// voice-hosting hide/if gate and the height/maxHeight factories read this
// one predicate.
const _voiceHosting = (s) => !!(s && s.root && s.root.voiceOpen && s.root.voiceHost === 'canvas')

// THE MODE CHIP STANDS DOWN WHERE THE HOME'S TABS ARE THE MODE (Nika
// 2026-10-23: "instead of [fly,dashboard,agent] rename [Main, Dashboard, Chat,
// Present, Build] … so therefore we might be able to remove dropdown from
// chatpromptarea on canvas and only keep in sidebar and other places"): both
// chip mounts — the pill's `Prefix` and the row's `AssistantModeChip` — hide
// while the home's tabs are on screen (the shell's workspace
// functions/homeView.js homeTabsOnScreen: the home itself, no pane over it).
// A mount's `hide` REPLACES the kit chip's own, so its rule is restated
// first: no mode writer here, no chip (the chip's scope reader, guarded — a
// host without the kit chip has none). The sidebar's chip and the composer's
// focus panel keep the mode switch; every other route keeps this chip.
const _modeChipHidden = (el, s) => {
  const offered =
    !!el &&
    !!el.scope &&
    typeof el.scope.modeChipOffered === 'function' &&
    !!el.scope.modeChipOffered(el, s)
  const fns = el && el.context && el.context.functions
  const tabs =
    !!fns &&
    typeof fns.homeTabsOnScreen === 'function' &&
    !!el.call('homeTabsOnScreen', s && s.root)
  return !offered || tabs
}

export const CanvasPromptTextarea = {
  extends: 'PromptTextarea',
  // FA204 / frank trap — SIZES used to be a bare module-scope object read
  // via bracket access (`SIZES[s.size]`) from inside the height/maxHeight
  // reactive factories below; it silently resolves to `undefined[...]`
  // (a throw, not just a dropped value) once this component is
  // frank.toJSON'd for platform delivery — the same
  // project_frank_drops_module_local_helpers class every VoiceOverlay/
  // VoiceControls/VoiceLog/VoiceStrip sibling in this file family has
  // already been swept for. Moved into scope, read via `el.scope.SIZES`.
  // the "+" menu's root mirror never outlives the composer (see _attachUpdate)
  onRemove: (el, s) => {
    if (s && s.root && s.root._promptMenuOpen && typeof s.root.update === 'function') {
      s.root.update({ _promptMenuOpen: false }, { preventFetch: true })
    }
  },
  scope: {
    // Per-size layout tokens. Width stays static at H across all three
    // states so the chrome doesn't reflow horizontally when expanding/
    // collapsing — only the vertical dimensions change.
    SIZES: {
      small: { height: 'auto', maxHeight: 'auto' },
      normal: { height: 'auto', maxHeight: '35dvh' },
      large: { height: '75dvh', maxHeight: '75dvh' }
    }
  },
  // Labeled complementary landmark: the composer floats at the shell root,
  // OUTSIDE every page landmark, so its content (Textarea, FreestyleThinking)
  // tripped axe `region` ("content not contained by landmarks") on every
  // route — MARKETPLACE-AUDIT residue, tickets/fable.md 2026-08-09. A
  // landmark role (not a widget role) never presentational-izes children,
  // and the unique label keeps `landmark-unique` clean. Same aria+attr
  // idiom as the file's ExpandBtn/SendBtn labels.
  role: 'complementary',
  aria: { label: (el) => el.call('polyglot', 'canvas.prompt.aria') },
  pointerEvents: 'auto',
  // Always a horizontal flex row (2026-06-08 per Nika "the flex is off,
  // its children should be horizontal"). Previously ONLY the conditional
  // `.isSmall` class set `flow: 'x'`; the moment that class didn't apply
  // — a reactive-class timing hiccup on the global shell-mounted home
  // instance, or any non-small size — the card fell back to
  // `display: block`, dropping the textarea + toolbar + send out of
  // their horizontal row (they stacked). A STATIC `flow` (not a reactive
  // factory — only static `flow` compiles to the `display:flex` classes
  // at create time; a factory just writes an inert style) guarantees
  // `display:flex; flex-direction:row` in EVERY state. Safe for the
  // expanded canvas composer too: there the toolbar + send are all
  // `position:absolute`, so the lone in-flow textarea simply fills the
  // row and the absolute buttons overlay exactly as before.
  flow: 'x',
  state: {
    size: 'small',
    images: [],
    draft: '',
    files: [],
    refs: [],
    dragOver: false,
    mentionPicker: null,
    attachOpen: false,
    buildArmed: false
  },
  position: 'absolute',
  bottom: 'X2',
  // Center horizontally: anchor both edges at 50% then pull back by half
  // the element's own width. Using `transform` (vs `left:0;right:0;margin:auto`)
  // keeps centering correct as the width animates between size states.
  left: '50%',
  // Horizontal centering (translateX) is combined with the virtual-keyboard
  // lift (translateY) in a SINGLE transform — two `transform` keys can't
  // coexist. `--kbd-inset` defaults to 0px (published by keyboardAvoidance.js on
  // focus) so this is an exact no-op on desktop and whenever the soft keyboard
  // is down; on iPad it raises the whole composer just above the keyboard. The
  // card's transition below names `transform` beside the size morph, so the
  // lift eases in on the same line — no second, size-animation-clobbering
  // transition line.
  transform: 'translateX(-50%) translateY(calc(-1 * var(--kbd-inset, 0px)))',
  zIndex: 10,
  // F2 — UNDER EVERY MODAL (current-2a 2026-09-28; the audit: "the composer
  // (z-index 1400) stays bright and clickable above the dimmed page behind
  // EVERY modal; its textarea takes focus outside the dialog"). The shell
  // mounts this composer as a fixed layer at 1400 — over the PageSlot window
  // (1300) on purpose — so a modal inside a page or a window, painting in THAT
  // layer's stacking context, could never lay its veil over it. The composer
  // answers "is a modal open" itself, from the kit's own markup: an AppDialog
  // or legacy Modal root carries [data-open] while open and its dialog carries
  // aria-modal="true" (a view stating data-open="false", the assistant board,
  // never counts). While one is open the composer drops to its own base z (10:
  // over the page, under the window backdrop (20), every modal veil and the
  // PageSlot sheet) and takes no pointer; both kits trap Tab in the dialog.
  // A declarative :has() rule — no listener, no state flag to keep in step.
  ':root:has([data-open]:not([data-open="false"]) [aria-modal="true"]) &': {
    zIndex: '10',
    pointerEvents: 'none'
  },
  // The home composer is the header's twin: the skins' `pill` theme
  // (HOME_AMBIENT_SKIN_SPEC §1, DESIGN-WL-SKINS-AMBIENT-DEFAULT-SOLID-
  // WHITELABEL-1) — the navbar glass in the solid skin, the card glass in
  // the ambient one. No border: the longhand zero stays from the
  // canvas-card-glass days (a base's border must not come back).
  theme: 'pill',
  borderWidth: '0',
  // THE CARD IS NOT A BACKDROP ROOT (dropdowns spec §7.2, measured live on dev 2ffb10c23): the `pill` theme puts
  // its blur ON this card, and an element with a backdrop-filter is a backdrop root for everything inside it —
  // the AI focus dropdown above (PromptBand -> SimoneSuggestions) then blurred only this card's own empty
  // surface and showed the page sharp through its glass. The card keeps the pill fill and ink; the SAME skin
  // blur moves to a layer under the content (the card is its own stacking context: position + zIndex), so the
  // card still frosts the page behind it and the dropdown's own glass reaches the page too.
  backdropFilter: 'none',
  '::before': {
    content: '""',
    position: 'absolute',
    inset: '0',
    borderRadius: 'inherit',
    zIndex: '-1',
    pointerEvents: 'none',
    backdropFilter: 'var(--theme-pill-backdropFilter)'
  },
  minHeight: 'auto',
  boxSize: 'auto',
  // MUST stay visible: PromptGallery (dropped refs/files) and
  // FreestyleThinking both float ABOVE this card via position:absolute +
  // bottom:100% — i.e. entirely OUTSIDE this element's box. A root
  // `overflow: hidden` (the founding value) clipped every pixel of both:
  // a dropped ticket ref updated state, laid out at full size, and showed
  // nothing (live-measured 2026-08-06: rect 493×32 at bottom:100%,
  // opacity 1, zero paint). Scroll clipping belongs to the inner boxes,
  // which already own it (textarea + history list).
  overflow: 'visible',
  // THE MOTION, NAMED (DESIGN-WL-SKINS-AMBIENT-DEFAULT-SOLID-WHITELABEL-1):
  // the size morph between small / normal / large — height, max-height,
  // min-height, width, min-width, padding, the capsule round — and the
  // keyboard lift (transform), on the B step. Never `all`: with `all` a skin
  // stamp that landed after first paint EASED the fill and the blur from the
  // solid pill to the ambient glass (measured on /files at 390, bad4186b3)
  // while the header pills beside it snapped. A skin or a scheme is a
  // surface, not a state change — fill, blur, ink, opacity and shadow snap.
  transition:
    'B height defaultBezier, B max-height defaultBezier, B min-height defaultBezier, ' +
    'B width defaultBezier, B min-width defaultBezier, B padding defaultBezier, ' +
    'B border-radius defaultBezier, B transform defaultBezier',
  // Size states swap `auto` for a token (card maxHeight auto→35dvh, the
  // Textarea's minHeight auto→E2 / height auto→100%). A transition cannot
  // interpolate to or from `auto`, so `B all` above animated ONLY the
  // paddings and radii while the box itself SNAPPED: measured live
  // 2026-09-02, 39px→124px at t=0 on Expand, paddings creeping for the
  // next 200ms (per Nika "fix canvasprompttextarea expand/minimize
  // animation"). `interpolate-size: allow-keywords` (CSS Values 5) makes
  // the auto↔length pairs interpolable, so the same `B all` now carries
  // the height too; engines without it keep today's snap. Inline `style`
  // (the AppPage.js precedent): css-in-props has no prop for it yet — FW
  // ticket FW-CSS-IN-PROPS-INTERPOLATE-SIZE-PROP-1.
  style: { interpolateSize: 'allow-keywords' },

  // Base width H (~524px) — shared footprint with the collapsed pill
  // (`.isSmall` minWidth below matches). The `large` composer widens to
  // `I` via the `.isLarge` conditional class — NOT a reactive `width`
  // factory: reactive LAYOUT props (width/flow) don't re-apply on a state
  // change in v3.14 (computed once at mount), whereas a conditional class
  // toggles a real className reactively, the same mechanism `.isSmall`
  // relies on.
  //
  // MUST be a bare spacing token, NOT `calc(var(--spacing-X) * N)`:
  // smbls' atomic CSS generator emits an EMPTY rule for a calc() width
  // string (`._w-<hash> { }` with no declaration), so the width silently
  // never applies and the element falls back to its ~344px content width.
  // That footgun is exactly why every multiplier tweak this session was a
  // no-op. Bare tokens (`G1`, `I`, …) compile to a real `width: var(--…)`
  // rule. Step the token to resize (G1 400 / G2 424 / G3 448 / H 524).
  width: 'H',
  // While the voice strip hosts (T18) the expanded card heights would leave a
  // mostly-empty 35/75dvh shell around a one-line strip — collapse to content
  // height for the hosting duration; the size state itself is untouched.
  height: (el, s) => (_voiceHosting(s) ? 'auto' : el.scope.SIZES[s.size].height),
  maxHeight: (el, s) => (_voiceHosting(s) ? 'auto' : el.scope.SIZES[s.size].maxHeight),
  '@mobileL': {
    left: 'A',
    right: 'A',
    bottom: 'A',
    width: 'auto',
    minWidth: '0',
    maxWidth: 'calc(100dvw - var(--spacing-B))',
    // Full-width at this breakpoint (left/right A), so no translateX centering —
    // but the keyboard lift still applies (phones raise a keyboard too).
    transform: 'translateY(calc(-1 * var(--kbd-inset, 0px)))'
  },

  // Connect to the local-agent bridge on mount. Idempotent — reconnects
  // automatically with a 2.5s backoff (see functions/freestyleBridge.js).
  onInit: (el) => {
    // Publish --kbd-inset so the translateY above tracks the soft keyboard on
    // touch devices. Idempotent singleton — the chat composer installs the same
    // one; calling from both is safe.
    installKeyboardAvoidance()
    el.call('freestyleConnect')
    // Plain instance counter (NOT state — purely internal bookkeeping, never
    // rendered) tracking native dragenter/dragleave nesting depth across the
    // composer's descendants. See onDragenter/onDragleave below for why a
    // counter is required instead of a single boolean.
    //
    // A DIRECT property on the element, not `el.scope.*`: `scope` is a
    // declared DOMQL feature, not a general-purpose bag, and it is undefined
    // on this component — writing through it threw
    // "Cannot set properties of undefined (setting 'dragDepth')" out of
    // onDrop, which killed the whole drop path before it read anything
    // (verified live in the console). Same instance-property technique as
    // ContextDropdown's `_ddSize`.
    el._dragDepth = 0
  },

  // Drag-and-drop attach — drop image file(s), any OTHER file, or a dragged
  // Symbols entity ref (ticket / project / project page / file — the shared
  // contract in shared/functions/dragRef.js) anywhere on the composer.
  //
  // Three payload shapes land in three different state buckets:
  //   - image files → state.images (data URL, rendered as a thumbnail — the
  //                    original behaviour, unchanged)
  //   - other files  → state.files (name/type/size ONLY — see the onDrop
  //                    comment below for why bytes are never read)
  //   - entity refs  → state.refs (the ref object itself)
  //
  // onDragenter/onDragover MUST preventDefault for a recognised drag (Files,
  // or our custom ref MIME) so the browser accepts the drop instead of
  // navigating to / opening the dragged content. hasDragFiles/hasDragRef are
  // TYPES-based checks (shared/functions/dragRef.js) — DataTransfer protected
  // mode blocks getData() until drop, but `types` is always readable, which is
  // why gating happens on types here; the actual ref/file payload is only
  // read in onDrop. A small (pill) composer does NOT expand on drop — the
  // PromptGallery floats above the composer in every size, so the dropped
  // thumbnail/chip is visible without resizing anything.
  //
  // dragDepth (a plain instance prop, NOT state — see onInit) implements the standard
  // enter/leave counter: moving onto a DESCENDANT (Textarea, PromptButtons,
  // …) fires a dragleave-on-parent + dragenter-on-child pair that both bubble
  // to these handlers (neither is stopped anywhere in the tree), so a naive
  // boolean would flip off while the pointer is still over the composer.
  // Counting nesting depth and only toggling the reactive `dragOver` state
  // flag at the 0⇄1 transition keeps the drop affordance (`isDragOver` /
  // `.isDragOver` below) stable for the whole hover, in every size.
  // Declared ref-drop target (shared/functions/dragRef.js REF_DROP_ATTR):
  // pointer-drag sources (the tickets board) hit-test for this attribute on
  // pointerup and deliver their ref through the native onDrop below via a
  // synthetic DragEvent — attach instead of committing their own move.
  'data-ref-drop': 'prompt',

  onDragenter: (e, el, s) => {
    if (!(el.call('hasDragFiles', e) || el.call('hasDragRef', e))) return
    e.preventDefault()
    el._dragDepth = (el._dragDepth || 0) + 1
    if (!s.dragOver) s.update({ dragOver: true })
  },
  onDragover: (e, el) => {
    if (!(el.call('hasDragFiles', e) || el.call('hasDragRef', e))) return
    e.preventDefault()
    if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy'
  },
  onDragleave: (e, el, s) => {
    el._dragDepth = Math.max(0, (el._dragDepth || 0) - 1)
    if (el._dragDepth === 0 && s.dragOver) s.update({ dragOver: false })
  },
  onDrop: (e, el, s) => {
    el._dragDepth = 0
    if (s.dragOver) s.update({ dragOver: false })

    // Entity ref first — a Symbols source (ticket/project/page/file card)
    // stamped this via el.call('setDragRef', …); readDragRef only resolves on
    // drop (protected mode) and returns null for any other kind of drag, so
    // falling through to the file handling below is always safe.
    const ref = el.call('readDragRef', e)
    if (ref) {
      e.preventDefault()
      const key = `${ref.kind}:${ref.id}`
      const existing = s.refs || []
      if (existing.some((r) => r.key === key)) return
      s.update({ refs: [...existing, { ...ref, key }] })
      return
    }

    const files = e.dataTransfer && e.dataTransfer.files
    if (!files || !files.length) return
    // MUST preventDefault as soon as we know we're handling this drop. The
    // previous image-only version deferred preventDefault until AFTER
    // filtering to images, so dropping a non-image file (0 images matched)
    // skipped preventDefault entirely and the browser fell through to its
    // native default action for an un-prevented drop — navigating the tab to
    // the dropped file — losing the whole canvas.
    e.preventDefault()

    const isImage = (f) => !!(f && f.type && f.type.indexOf('image') !== -1)
    const imgs = Array.prototype.filter.call(files, isImage)
    const others = Array.prototype.filter.call(files, (f) => !isImage(f))

    if (imgs.length) {
      // Capture name/mime/size alongside the data URL — matches the `files`
      // bucket's shape (`{key, name, mime, size}`) below, and is what lets
      // _sendPrompt describe an attached image in the prompt's text context
      // instead of silently dropping it (see that function's comment).
      Promise.all(
        imgs.map(
          (file) =>
            new Promise((res) => {
              const reader = new FileReader()
              reader.onloadend = () => res({ file, dataUrl: reader.result })
              reader.readAsDataURL(file)
            })
        )
      ).then((results) => {
        const existingImages = s.images || []
        const additions = []
        for (const { file, dataUrl } of results) {
          if (!dataUrl) continue
          const key = `${file.name}:${file.size}:${file.lastModified}`
          if (existingImages.some((ex) => ex.key === key)) continue
          if (additions.some((ex) => ex.key === key)) continue
          additions.push({
            key,
            name: file.name,
            mime: file.type || 'image/*',
            size: file.size,
            dataUrl
          })
        }
        if (additions.length) s.update({ images: [...existingImages, ...additions] })
      })
    }

    if (others.length) {
      // Metadata only — NOT a data URL. Unlike images (see the imgs.length
      // branch above), non-image files have no bridge-side consumer even in
      // principle today — freestyleSend's `attachments` field is read by
      // nothing downstream yet for ANY kind (see its header comment), and
      // reading a large drop (a 50MB PDF) into a base64 data URL would just
      // burn memory for a payload nothing will use. name/type/size is enough
      // to surface the file as real context text on send.
      const existingFiles = s.files || []
      const additions = []
      for (let i = 0; i < others.length; i++) {
        const f = others[i]
        const key = `${f.name}:${f.size}:${f.lastModified}`
        if (existingFiles.some((ex) => ex.key === key)) continue
        if (additions.some((ex) => ex.key === key)) continue
        additions.push({
          key,
          name: f.name,
          mime: f.type || 'application/octet-stream',
          size: f.size
        })
      }
      if (additions.length) s.update({ files: [...existingFiles, ...additions] })
    }
  },

  isSmall: (el, s) => s.size === 'small',
  '.isSmall': {
    flow: 'x',
    align: 'center start',
    gap: '0',
    padding: '0',
    // Resting pill = a chrome island: share the header islands' exact gauge
    // (HEADER_ISLAND_MIN_HEIGHT, the HeaderLeft/Tabs/Right minHeight) and
    // their round family (radiusPill, the IconNavbar/HeaderTabs pill round)
    // instead of a content-driven ~3px-shorter bar on PromptTextarea's card
    // round (radiusCard). radiusPill clamps to the same capsule today, but
    // keeps capsule parity with the pills if the bar ever grows past the
    // card round's clamp range (iPad, 2026-08-01 per Nika "canvas prompt
    // textarea not consistent with others"). Expanded sizes keep the
    // inherited card round — a pill radius on the 35dvh card would clip the
    // Z-inset corner buttons under overflow:hidden.
    //
    // ON A TOUCH SCREEN THE PILL IS A 44pt TARGET (DESIGN-SHELL-RESPONSIVE-IOS-
    // SAFARI-1; decided 2026-10-05: 'make the composer pill 44 px on touch
    // screens', desktop unchanged). The 36px gauge left a 33px field: a tap a
    // little above or below the text missed it on a phone. On touch the pill
    // floors at `controlH` and its field grows to fill it (the Textarea's own
    // vertical inset, below), so the whole pill between its buttons is the
    // field.
    minHeight: (el) => (_touchScreen(el) ? 'controlH' : getHeaderIslandMinHeight()),
    boxSize: 'auto',
    minWidth: 'H',
    '@mobileL': {
      minWidth: '0',
      width: 'auto'
    }
  },

  // Capsule round only while the minimized pill is ONE line (2026-09-02 per
  // Nika "fix round when minimized ai textarea has multiple lines - make
  // max round just like it has on expanded"). `.isSmall` used to pin
  // radiusPill (999px) unconditionally; the browser clamps that to half the
  // box height — right for the one-line resting pill, but as the
  // small-state autoGrow grows the box with a multi-line draft, the 999px
  // kept clamping to half of an ever-taller box and the capsule turned into
  // a stadium/oval. The pill round now lives in ITS OWN conditional class
  // gated on single-line content, so the multi-line pill simply INHERITS
  // the brand PromptTextarea card round — the exact radius the expanded
  // sizes show (no new radius literal here, per the design-language lock).
  // The newline test re-evaluates on every draft update; the line-height
  // probe catches wrapped long lines with no newline in them.
  // THE WRAP PROBE IS A LAYOUT READ, and a conditional class is re-read on
  // EVERY render pass of this element — including passes that have nothing to
  // do with the composer. `clientHeight` + `getComputedStyle` in the middle of
  // a pass flush style and layout for the whole page: MEASURED on dev
  // 2026-10-02 (/w/vault/default, Chrome MCP) at 204 ms on a loaded box and
  // 33-38 ms on a quiet one — one of three full relayouts the first board
  // submit paid for before its skeleton reached the DOM
  // (DESIGN-WL-ASSISTANT-BOARD-SUBMIT-BLOCKS-THE-MAIN-THREAD-FOR-A-SECOND-1).
  // So the probe answers from the draft wherever the draft already decides it,
  // and measures at most ONCE per draft:
  //   • an EMPTY box is one line — exact, no measurement (this is the submit
  //     case: the send clears the draft, so every submit used to re-measure);
  //   • a draft with a newline is multi-line — exact, as before;
  //   • anything else keeps the last verdict for that exact draft, answered
  //     before the node is touched, so an unrelated pass reads nothing. A
  //     keystroke is still measured — the only thing that re-wraps the line.
  isPillRound: (el, s) => {
    if (s.size !== 'small') return false
    const draft = s.draft || ''
    if (/\n/.test(draft)) return false
    if (!draft) return true
    const kept = el.__pillRoundFor
    if (kept && kept.draft === draft) return kept.round
    const ta = el.node && el.node.querySelector && el.node.querySelector('textarea')
    // ONE read of the box, not two: `clientHeight` is a layout read, and the
    // guard and the line test both used to ask for it.
    const box = ta ? ta.clientHeight : 0
    if (!box) return true
    const lh = parseFloat(window.getComputedStyle(ta).lineHeight)
    const round = !(lh && box > lh * 1.6)
    el.__pillRoundFor = { draft, round }
    return round
  },
  '.isPillRound': {
    round: 'radiusPill',
    // The line pill's centring, declared HERE as well as in .isSmall's
    // `align`: a case that turns off clears the inline keys it owns, and a
    // still-on case (.isSmall) is not re-run to put its value back
    // (FW-ELEMENT-CASE-TURN-OFF-CLEARS-A-STILL-ON-CASES-KEY-1). On the small
    // pill .isPillRound is the exact complement of .isPillWrapped, so the two
    // flip in the same flush and this one re-writes `center` — live on dev
    // a0d036b a cleared wrapped draft (every long send) left `align-items:
    // normal`: the send 0px from the pill's top, 3.1px from its bottom.
    alignItems: 'center'
  },

  // A WRAPPED PILL keeps its controls on its bottom line (Nika 2026-10-18,
  // "improve UI of chat textarea"): a draft that wraps grows the small pill
  // around the text, and the row's centre alignment left the mode chip and
  // the buttons floating at the middle of the grown box (measured live: a
  // 191px pill, controls at its centre). Anchored to the bottom, `X` above
  // the edge — the same inset the chip keeps from the start and send from the
  // end — they stay where the eye left them, like the expanded composer's
  // docked row. Reads isPillRound's kept verdict for this exact draft (no
  // second layout read); the exec fallback covers a pass that reads this case
  // first.
  isPillWrapped: (el, s) => {
    if (s.size !== 'small') return false
    const draft = s.draft || ''
    if (!draft) return false
    if (/\n/.test(draft)) return true
    const kept = el && el.__pillRoundFor
    if (kept && kept.draft === draft) return !kept.round
    if (!el || typeof el.call !== 'function') return false
    return !el.call('exec', el.isPillRound, el)
  },
  '.isPillWrapped': {
    alignItems: 'flex-end',
    paddingBottom: 'X'
  },

  // `large` widens the prompt a little (G1 → I) via a conditional class
  // (reliable across the size change — see the width comment above).
  isLarge: (el, s) => s.size === 'large',
  '.isLarge': {
    width: 'I',
    minWidth: 'I',
    '@mobileL': {
      width: 'auto',
      minWidth: '0',
      height: '58dvh',
      maxHeight: '58dvh'
    }
  },

  // Drop affordance while a recognised drag (file or entity ref) is over the
  // composer, in EVERY size (independent of .isSmall/.isLarge, which only
  // touch width/round — this block composes fine alongside either).
  //
  // Fill-contrast, not a ring (design-direction.md §8 — borders/outlines are
  // banned outright, separation is fill contrast alone; the only sanctioned
  // stroke is the dashed inactive-state ring, and a drag target is an ACTIVE
  // state, which the same law spells out as a SOLID fill, never a stroke).
  // `selected` — the SAME semantic "this is engaged" wash VoiceBtn.isActive
  // and CvSelectionChip already use in this exact file, not a bespoke hue:
  // a raw `blue.12` here was caught by the raw-hue-ink lock
  // (shared/__tests__/designLanguageLocks.scan.js, Rule 69 — a raw CHROMATIC
  // hue as background needs a semantic reach first) and reusing the house
  // active-state token is BOTH the fix and the better call — the composer's
  // "something is engaged here" language now reads identically whether it's
  // voice or a drop target, one grammar instead of two.
  isDragOver: (el, s) => !!s.dragOver,
  '.isDragOver': { background: 'selected' },

  // ── THE BAND — one owner of the strip above the composer card (O1, opus.md).
  // Before: four absolutely-positioned siblings (PromptGallery, SelectionChips,
  // FreestyleThinking, FreestyleStatus) + the shell's position:fixed
  // SimoneSuggestions all fought for the same strip in three visual grammars.
  // Now ONE positioned band holds the awareness bar and the suggestion row as
  // ORDERED children: image thumbnails (bytes never leave the browser — the bar
  // labels them "not sent") · AiContextBar (scope line + focus/attached chips,
  // one grammar) · SimoneSuggestions (in-flow here, no longer fixed over the
  // composer). Still anchored bottom:100% so the composer's own rect — which
  // the shell's --prompt-clear ResizeObserver measures — is unchanged.
  PromptBand: {
    position: 'absolute',
    bottom: '100%',
    left: '0',
    right: '0',
    marginBottom: 'Y',
    flow: 'y',
    gap: 'Y',
    minWidth: '0',
    pointerEvents: 'none',
    // Children take pointer events back; the band itself must not eat clicks
    // on whatever sits behind its empty area.
    childProps: { pointerEvents: 'auto' },

    ImageThumbs: {
      show: (el, s) => (s.images || []).length > 0,
      flow: 'x',
      gap: 'Z',
      flexWrap: 'wrap',
      children: (el, s) =>
        (s.images || []).map((img) => ({
          key: img.key,
          tag: 'img',
          src: img.dataUrl,
          title: img.name
        })),
      childProps: {
        boxSize: 'D',
        round: 'radiusCard',
        objectFit: 'cover',
        background: 'overlay'
      }
    },

    // The AI ISLAND — ONE glass surface unifying the scope line, the
    // awareness chips (it hosts AiContextBar itself, scope-line off) and the
    // suggestion capsules (2026-08-26 per Nika "unify suggestions and
    // path"). Focus-gated paint, composer width, in-flow. The band no
    // longer mounts AiContextBar separately — the island owns the whole
    // strip except the image thumbs above.
    SimoneSuggestions: {}
  },

  VoiceStrip: {
    hostKey: 'canvas',
    minHeight: () => getHeaderIslandMinHeight()
  },

  // THE PILL'S MODE CHIP — ONE row: [mode chip] [textarea] [send] (the
  // anatomy agreed with the dropdowns lane, 2026-10-01). DOMQL mounts children
  // in the MERGED key order and the brand PromptTextarea's keys come first, so
  // the chip fills the brand's leading slot `Prefix` (brand 3.14.801, design-
  // d2's 5934886): DOM order = screen order = Tab order — never a CSS `order`.
  // (With an older brand there is no slot and the key mounts after the
  // inherited Textarea: still reachable, just later.) It is the kit chip
  // (`extends`: the key names no component) in its compact form (glyph +
  // caret, "Mode: <mode>"); it shows only in the small pill — the normal /
  // large composer keeps the chip first in its docked button row below, and
  // that one stands down in the pill. Each still hides itself without the
  // mode writer (no mount-site gate).
  // ONE TRIGGER FOR CONTEXT + MODE (Nika 2026-10-07: "merge two dashboard and
  // context switcher dropdowns into one in aipropttextarea"): both chip mounts
  // ask the kit for its merged form (`withContext`) — the glyph plate, the
  // context's short name, the caret; one menu, Context then Mode. The context
  // pill that sat after the text (AiScopePill) is gone. Without the shell's
  // context readers the chip stays the compact mode chip.
  Prefix: {
    extends: 'AssistantModeChip',
    withContext: true,
    // the home's tabs are the mode there (see `_modeChipHidden`)
    hide: (el, s) => _modeChipHidden(el, s),
    // A mount-site class object REPLACES the kit chip's own, so its
    // `isCompact` (the glyph + caret form) is restated here — compact only
    // where no context is named (the merged form shows the context's name).
    // The kit's reader is guarded: a host that mounts this composer without
    // the kit chip registered has no such scope method, and a throw here
    // broke the whole composer (canvasPromptExpandedFloor, 10 tests).
    class: {
      // Not while the call strip hosts: the bar is the call then, and the
      // chip's context and mode say nothing about it.
      isPillSlot: (el, s) => s.size === 'small' && !_voiceHosting(s),
      isCompact: (el, s) =>
        s.size === 'small' &&
        !(
          el.scope &&
          typeof el.scope.modeChipContextRow === 'function' &&
          el.scope.modeChipContextRow(el, s)
        )
    },
    display: 'none',
    '&.isPillSlot': { display: 'flex' },
    flexShrink: '0',
    marginLeft: 'X'
  },

  Textarea: {
    // Hidden (not unmounted) while the voice strip hosts this composer — a
    // reactive `hide` on a mounted-open child, the UploadBtn precedent.
    hide: (el, s) => _voiceHosting(s),
    // Return-to-typing entrance, symmetric with VoiceStrip's own
    // `animation: 'fadeInUpShort'` — see that file's comment for why an
    // `animation` (not `transition`) is the fix for a display:none-gated
    // swap. Plain `fadeIn` (opacity only, no rise) here: this is the
    // familiar "home" state reappearing, not a new surface arriving, so a
    // quieter entrance than the strip's is the right asymmetry.
    animation: 'fadeIn',
    minWidth: (el, s) => (s.size === 'small' ? 'auto' : '100%'),
    resize: 'none',
    rows: 1,
    // App composer is a chat input, not the landing hero: the brand
    // PromptTextarea centers text at @mobileM (≤560px) for the marketing
    // page — on iPhone that centered the "Whats up?" placeholder
    // (2026-08-02 per Nika "make whats up align left"). Left at every
    // width here; the landing keeps its own centering. The hero's phone
    // padding stands down here too — see the '@mobileL' block below.
    textAlign: 'left',
    '@mobileM': { textAlign: 'left', padding: null },
    // Focus mirror for the SimoneSuggestions focus-gated chips (2026-08-06
    // per Nika — chips show on focus, not as a standing overlay). Blur
    // clears on a 250ms delay so a chip click (blur fires at its
    // pointerdown, the click ~100-150ms later) still sees the flag up;
    // refocus within the window cancels the clear.
    onFocus: (e, el, s) => {
      if (el.parent && el.parent._blurT) {
        clearTimeout(el.parent._blurT)
        el.parent._blurT = null
      }
      if (s.root && !s.root._promptFocused) s.root.update({ _promptFocused: true })
      // A fresh focus brings back a panel put away (shared functions/
      // aiPanelFocus.js: Escape writes the flag AFTER handing focus here).
      if (s.root && s.root._aiPanel === 'dismissed') {
        s.root.update({ _aiPanel: null }, { preventFetch: true })
      }
    },
    // The focus panel's way in, said to assistive technology: ↑ in an empty
    // field opens it (shared functions/aiComposerKeys.js).
    ariaDescription: (el) => el.call('polyglot', 'ai.panel.fieldHint'),
    onBlur: (e, el, s) => {
      const host = el.parent
      if (!host) return
      if (host._blurT) clearTimeout(host._blurT)
      host._blurT = setTimeout(() => {
        host._blurT = null
        if (s.root && s.root._promptFocused) s.root.update({ _promptFocused: false })
        // Close the mention picker on a settled blur — the 250ms window is
        // exactly what lets a dropdown-row click land first (its
        // onPointerdown preventDefault keeps focus in most cases; this is
        // the belt for the ones where it doesn't).
        if (s.mentionPicker) s.update({ mentionPicker: null })
      }, 250)
    },
    // Bind value to state.draft so _sendPrompt reads/clears via state (FA511).
    value: (el, s) => s.draft || '',
    // No `height` factory (2026-09-29, design-d2's review): `height` has ONE
    // owner — the framework's `autoGrow` below, with `_settleTextareaSize`
    // fitting it through a size flip. The old `small ? 'auto' : '100%'`
    // factory was a second writer whose value never survived: autoGrow
    // re-measured it away in small/normal, and in `large` the 75dvh
    // min-height floor already fills the 75dvh card.
    // `auto` in every size (2026-10-01, the composer-heights matrix): the
    // pill's `hidden` clipped a draft past the max-height cap (12 lines at
    // 1440: 265 px shown of 299) with NO way to scroll to the rest — and a
    // height the autosize did not re-measure (a programmatic draft, a width
    // change) clipped the same way. Below the cap autoGrow pins the exact
    // content height, so no scrollbar shows.
    overflow: 'auto',
    // THE SIZE MORPH, NAMED — `height` left out on purpose (2026-09-29).
    // `height` belongs to `autoGrow` below, which collapses the box to
    // `auto` and reads `scrollHeight` in the SAME tick. Under the `B all`
    // this line used to be, that write started a height transition, and a
    // transitioning height (interpolate-size makes auto interpolable) still
    // holds its OLD value at that read: the measure was floored by the
    // current box, so the box could grow but never shrink (measured on
    // dev.my: the small pill 39→140 px on five lines, still 140 px after
    // deleting every line). A size flip changes min-height, max-height,
    // min-width, the paddings and flex-grow on this box — those keep the B
    // step, so Expand/Minimize is the same motion; content growth is
    // instant, like any chat input. Named, never `all`: a skin switch does
    // not ease this ink either (the card's own law, above).
    transition:
      'B min-height defaultBezier, B max-height defaultBezier, ' +
      'B min-width defaultBezier, B padding defaultBezier, B flex-grow defaultBezier',
    // Same interpolate-size opt-in as the card (see its comment): this is
    // the box whose minHeight auto↔E2 actually drives the card's height.
    style: { interpolateSize: 'allow-keywords' },
    // THE FLOOR. `normal` E → E2 (2026-09-02 per Nika "when expanded, make
    // it a little bit taller"): one step up the spacing scale, 162 px live.
    // `large` stays on its viewport fraction. A reactive factory, so the
    // framework writes it INLINE and re-writes it on a size change only —
    // no project code may write or clear this box's min-height. The old
    // normal/large onInput growth cleared it on every keystroke: expand,
    // type one character, and the card fell from 162 px to one row
    // (2026-09-29 per Nika "when we expand canvaschattextarea and start
    // typing it shrinks the height").
    minHeight: (el, s) => ({ small: 'auto', normal: 'E2', large: '75dvh' })[s.size],
    maxHeight: (el, s) => ({ small: '35dvh', normal: '35dvh', large: '75dvh' })[s.size],
    // Content growth is the framework's declarative `autoGrow` (reactive
    // form — tickets/fable.md "no declarative primitive for textarea
    // auto-grow"), armed in `small` AND `normal`: its input listener sets
    // height to the content's scrollHeight, and the cascade bounds it — the
    // `minHeight` floor above (auto / E2) and the `maxHeight` cap (35dvh).
    // It writes `height` only, so the floor survives every keystroke and a
    // shrinking draft returns the box to the floor, never below it. `large`
    // is a fixed 75dvh box that scrolls, so it stays disarmed. On a size
    // change the factory re-runs: leaving for `large` DISARMS and clears the
    // behavior's own inline height; coming back re-arms and re-measures.
    autoGrow: (el, s) => s.size !== 'large',
    // THE PLACEHOLDER NEVER SIZES THE BOX. An EMPTY textarea's scrollHeight
    // includes its placeholder laid out across lines: measured live on dev
    // 63151a91f, a placeholder wider than the field took the empty pill from
    // 37 px to 132-156 px, and `autoGrow` pins exactly that scrollHeight —
    // the "weird heights" of an empty composer (any language, any size,
    // whenever the shown line is wider than the field: before the measured
    // fit runs on the first render, a field that narrowed, a late font).
    // One line, never wrapped: the empty box is one row tall in every case.
    // Not a cut: the line is chosen to fit the field
    // (shared functions/assistantModePlaceholder.js assistantModePlaceholderFit).
    '::placeholder': { whiteSpace: 'nowrap' },
    // NEVER STRETCHED BY THE CARD (2026-10-01, the composer-heights matrix).
    // The card is a flex ROW whose only in-flow child is this textarea, and a
    // row stretches its items to its own height. autoGrow reads scrollHeight
    // with `height: auto` — on a stretched item that is the CARD's height, not
    // the text's — and writes it back: measured live on 1a2612ab9 at 614 px,
    // an EMPTY normal composer read 219 px with `height: auto` (7.2 lines)
    // while the same box with no stretch measured 144 px (its E2 floor). A
    // box that writes back its own stretched size can only keep it or grow.
    // `flex-start` takes the card out of the measure: the height is the text
    // + padding, bounded by the min-height floor and the max-height cap.
    alignSelf: 'flex-start',
    // The resting pill's field: `Y` above and below on a desktop (the 36px
    // header gauge), `Z1` on a touch screen — a 20.8px line + 2 x 12.2px fills
    // the 44pt pill (the card's `.isSmall` floor) in proportion, text centred
    // (DESIGN-SHELL-RESPONSIVE-IOS-SAFARI-1). The expanded sizes keep theirs.
    paddingTop: (el, s) => (s.size === 'small' ? (_touchScreen(el) ? 'Z1' : 'Y') : 'A'),
    paddingBottom: (el, s) => (s.size === 'small' ? (_touchScreen(el) ? 'Z1' : 'Y') : 'C'),
    paddingLeft: (el, s) => (s.size === 'small' ? 'A' : 'A'),
    paddingRight: (el, s) => (s.size === 'small' ? 'A' : 'A2'),
    // Match the global Search input's text size (1.0625rem) so the prompt
    // and the header search read at exactly the same size (2026-06-09 per
    // Nika "set same size to text to Search and CanvasPromptTextarea").
    // Fixed, not size-reactive — they should match in every prompt state.
    fontSize: '1.0625rem',
    // A typed draft in the title ink: the field's inherited text ink
    // (rgba(0,0,0,.65)) read like its placeholder (rgb(85,85,93)). The
    // placeholder keeps its own rule.
    color: 'title',
    flexGrow: (el, s) => (s.size === 'small' ? '1' : undefined),
    // THE SIZE FACTORIES ABOVE OWN THE PADDING AT EVERY WIDTH. The brand
    // PromptTextarea pads its hero field `B B - B` at @mobileL and `D B - B`
    // at @mobileM. A media block that states a property this element writes
    // inline (the reactive longhands above, shorthand family included) is
    // emitted `!important` by the framework, so on a phone the hero padding
    // beat the pill's: measured on dev 2026-10-18 at 390 px, the field padded
    // 67.8 / 25.9 / 6.1 px, the open composer stood 95 px tall and its text
    // sat under the mode chip and the buttons (Nika: "it looks too big and
    // ugly now"). `null` keeps the inherited declaration out — this
    // component's key wins the extends merge and a CSS block skips a null.
    '@mobileL': {
      fontSize: '1rem',
      minWidth: '0',
      padding: null
    },

    onInput: (e, el, s) => {
      // Persist the draft to state so _sendPrompt can read it without touching el.node.
      s.update({ draft: e.target.value })
      // Mention typeahead — after the draft write so the picker's splice
      // math and the state body agree (see _refreshAiMention).
      _refreshAiMention(el, s)
      // No height work here, in any size: the declarative `autoGrow` above
      // owns content growth through its own input listener, and the
      // `minHeight` floor and `maxHeight` cap bound it. The normal/large
      // branch that lived here (FA511 el.node writes) cleared
      // `el.node.style.minHeight` on every keystroke — the framework's
      // inline E2 floor — so the expanded composer shrank to one row on the
      // first character (2026-09-29 per Nika).
    },

    // The open panels get every key FIRST — the '+' attach menu's arrows and
    // the whole mention-picker traversal (Arrow/Home/End/Enter/Tab/Escape).
    // That model is `aiComposerKeydown` in shared, not inline here, because
    // the shell mount of this composer REPLACES this handler wholesale
    // (workspace/app.js) and has to run the identical branches — see the
    // `_aiComposerKeydown` note at the top of this file.
    //
    // It returns true when it consumed the key. Enter is consumed ONLY while a
    // picker with options is open (it inserts the mention), so with no picker
    // open Enter still falls through to the send below — one send, never two.
    // Shift+Enter inserts a newline, as before.
    onKeydown: (e, el, s) => {
      if (_aiComposerKeydown(e, el, s)) return
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault()
        _sendPrompt(el)
      }
    },

    onPaste: (e, el, s) => {
      const items = e.clipboardData?.items
      if (!items) return
      for (let i = 0; i < items.length; i++) {
        if (items[i].type.indexOf('image') !== -1) {
          e.preventDefault()
          const blob = items[i].getAsFile()
          if (!blob) continue
          // Capture name/mime/size alongside the data URL — same shape as
          // the onDrop images branch above (see its comment).
          const mime = blob.type || items[i].type || 'image/png'
          const name = blob.name || `pasted-image.${(mime.split('/')[1] || 'png').split('+')[0]}`
          const size = blob.size
          const reader = new FileReader()
          reader.onloadend = () => {
            const key = `${name}:${size}:${Date.now()}`
            const images = [...(s.images || []), { key, name, mime, size, dataUrl: reader.result }]
            s.update({ images })
          }
          reader.readAsDataURL(blob)
        }
      }
    }
  },

  // Reasoning-log panel — slides in above the prompt while freestyleBusy is true.
  // Streams accumulated assistant_delta text from s.root.freestyleStream.
  // Height is always E (~108px); collapsed to height:0/opacity:0 when idle so
  // the entrance animation triggers on every new turn without a remount.
  FreestyleThinking: {
    position: 'absolute',
    bottom: '100%',
    left: '0',
    right: '0',
    marginBottom: 'Y',
    theme: 'canvas-card',
    borderRadius: 'A',
    padding: 'Z',
    fontSize: 'X2',
    color: 'paragraph',
    // Streaming reasoning log — role=log (implicit polite live region) with
    // a label, so the focusable scroll panel has an accessible name.
    role: 'log',
    aria: { label: (el) => el.call('polyglot', 'canvas.prompt.reasoningAria') },
    // Busy-COUPLED focusability (axe `scrollable-region-focusable`, serious —
    // MARKETPLACE-AUDIT residue, tickets/fable.md 2026-08-09): the panel is
    // height-0/opacity-0 when idle, so a STATIC tabindex would mint an
    // invisible tab stop. `0` only while freestyleBusy makes it actually
    // scrollable/visible; `-1` when idle is never a tab stop but keeps the
    // attribute's reactive lifecycle simple (same freestyleBusy predicate the
    // height/opacity/transform factories below already re-run on).
    tabindex: (el, s) => (s.root?.freestyleBusy ? '0' : '-1'),
    // collapsed → expanded driven by freestyleBusy
    height: (el, s) => (s.root?.freestyleBusy ? 'E' : '0'),
    opacity: (el, s) => (s.root?.freestyleBusy ? '1' : '0'),
    // starts ~42px (C) below final position, slides up on enter
    // var(--spacing-C) form required — DOMQL token resolver does not reach
    // inside CSS function args (calc/clip-path/translateY use var() form).
    // UPPERCASE: the DS emits `--spacing-C` and custom-property names are
    // case-sensitive, so the lowercase form this used resolved to the empty
    // string and `translateY()` was invalid — measured, the whole transform
    // computed to `none`, so this never actually slid.
    transform: (el, s) =>
      s.root?.freestyleBusy ? 'translateY(0)' : 'translateY(var(--spacing-C))',
    transition: 'B all defaultBezier',
    // Scrollable only while OPEN — same busy predicate as tabindex above.
    // Idle, the height-0 box still had scrollHeight > clientHeight, so axe's
    // scrollable-region matcher saw an invisible scrollable region regardless
    // of tabindex; a collapsed panel has no business scrolling at all.
    overflow: (el, s) => (s.root?.freestyleBusy ? 'hidden auto' : 'hidden'),
    pointerEvents: (el, s) => (s.root?.freestyleBusy ? 'auto' : 'none'),
    text: (el, s) => s.root?.freestyleStream || el.call('polyglot', 'canvas.prompt.thinking'),
    // Auto-scroll to bottom as new delta text arrives. `.scrollTo()` (a
    // method call), not a `.scrollTop =` assignment — FA511 treats el.node
    // as read-only from project code; `.focus()`/`.blur()`/`.scrollTo()`/
    // `.setPointerCapture()` are the sanctioned exceptions. Behaviourally
    // identical (no `behavior: 'smooth'`, so this is still an instant jump,
    // exactly as before) — a safe swap, not a rewrite of the mechanism.
    onRender: (el) => {
      if (el.node) el.node.scrollTo(0, el.node.scrollHeight)
    }
  },

  // Thinking sheen — a subtle shimmer that sweeps the composer while the AI is
  // working: a GENERAL assistant turn (s.root.chatTyping) OR the build/bridge flow
  // (freestyleBusy). Makes the input itself feel alive, not just the pill above
  // it. The gradient + keyframe live in app.js's injected shell CSS keyed on
  // [data-key="ThinkingSheen"] — a raw gradient/animation string empty-rules
  // through the DOMQL atomic compiler (same footgun as the pill's `bottom`).
  // pointerEvents:none so it never blocks typing a follow-up mid-turn; the
  // composer's overflow:hidden clips it to the card radius.
  ThinkingSheen: {
    // `chatTyping ||` short-circuits away `freestyleBusy` whenever chatTyping
    // is truthy, and `s.root &&` can drop both — so the sheen could stick on
    // or never appear depending on which flag moved first. FA515.
    hideDeps: ['chatTyping', 'freestyleBusy'],
    hide: (el, s) => !(s.root && (s.root.chatTyping || s.root.freestyleBusy)),
    pointerEvents: 'none'
  },

  // Bridge-down hint — shown when the agent is not busy but the bridge is
  // disconnected or errored. Separate from the panel so the hint stays visible
  // in the collapsed state.
  FreestyleStatus: {
    tag: 'span',
    hide: (el, s) =>
      s.root?.freestyleBusy ||
      (!s.root?.freestyleError &&
        s.root?.freestyleStatus !== 'disconnected' &&
        s.root?.freestyleStatus !== 'error'),
    position: 'absolute',
    bottom: '100%',
    left: '50%',
    transform: 'translateX(-50%)',
    marginBottom: 'Y',
    fontSize: 'X2',
    color: (el, s) =>
      s.root?.freestyleError || s.root?.freestyleStatus === 'error' ? 'error' : 'paragraph',
    opacity: '.75',
    whiteSpace: 'nowrap',
    pointerEvents: 'none',
    // The error branch is the FIRST read, so any run that returns before it
    // (or with `s.root` unset) leaves `freestyleError` untracked — the hint
    // then never updates when an error actually arrives, which reads to the
    // user as the app silently doing nothing. FA515.
    textDeps: ['freestyleError'],
    text: (el, s) => {
      if (s.root?.freestyleError) return s.root.freestyleError
      if (s.root?.freestyleStatus === 'disconnected' || s.root?.freestyleStatus === 'error') {
        return el.call('polyglot', 'canvas.prompt.bridgeDown')
      }
      return ''
    }
  },

  PromptButtons: {
    // Hidden while the voice strip hosts (T18) — the strip carries its own
    // controls. NOTE: hiding mid-PTT-hold is safe — the VoiceBtn's pointer
    // capture keeps routing pointerup/pointercancel to the captured target
    // while it stays in the document (display:none does not release capture).
    hide: (el, s) => _voiceHosting(s),
    // Return-to-typing entrance, symmetric with Textarea's — see its
    // comment above.
    animation: 'fadeIn',
    flow: 'x',
    align: 'center start',
    fontSize: 'Z1',
    transition: 'B all defaultBezier',
    childExtends: 'NavbarButton',
    childProps: {
      // Was a `s.size === 'small' ? 'Y' : 'Y2'` factory — frozen at mount
      // like the layout five below, so a small-mounted composer always
      // rendered Y anyway. Static Y keeps today's rendered look in every
      // state; per-size button padding can come back as a case on the
      // buttons themselves if the design wants it.
      padding: 'Y',
      transition: 'B all defaultBezier',
      // A thumb target on a phone: the row's buttons measured 27px in a 390px
      // frame (QA mobile pass, 2026-09-23) against the 36px compact control.
      '@mobileL': {
        minWidth: 'controlHCompact',
        minHeight: 'controlHCompact'
      }
    },

    // VOICE-TALK-ONLY-SIMONE-1 part 1 (Nika: "fix PromptViewButtons … when
    // its expanded"): these five were `(el, s) => (s.size === 'small' ? … : …)`
    // FACTORIES. Registry components freeze reactive layout factories at
    // mount in v3.14 — the exact rule this file's own width comment (~200)
    // cites for using `.isSmall`/`.isLarge` conditional classes instead — so
    // a composer that mounted small kept `position: static` forever: on
    // expand the toolbar stayed IN-FLOW inside the flex row, crushed against
    // the textarea, instead of docking to the card's bottom-left. Same
    // conditional-case mechanism as the root card now: base = the small-pill
    // geometry, `.isDocked` = every non-small size. (gap/padding smalls
    // ride along so the docked toolbar gets its roomier spacing too.)
    isDocked: (el, s) => s.size !== 'small',
    position: 'static',
    marginRight: 'Y',
    gap: 'X',
    '.isDocked': {
      position: 'absolute',
      bottom: 'Z',
      left: 'A',
      // The row ENDS one X before MinimizeBtn — its offset (D), its box
      // (controlHCompact) and a gap, on the same em (both are fontSize Z1),
      // so no tool can sit under minimize or send at any width. `C2` reached
      // inside minimize: at 390 the AI settings click landed on it (design-d2,
      // dev 3691a9c28). Tools that do not fit MOVE (Upload → the + menu;
      // Inspect off the phone row) — never overlap, never cut.
      right: 'calc(D + controlHCompact + X)',
      marginRight: '0',
      gap: 'Z',
      // Phones: the docked row keeps the pill's X gap. The Z gap applied only
      // when the row was CREATED docked (the expanded composer) and cost a
      // project page's 390 row 5 × 5.8 px it does not have (design-d2's joint
      // pass: 260.9 px row, 237.6 needed at X). This media step emits with the
      // conditional class doubled, so it holds in both docked states.
      '@mobileL': { gap: 'X' }
    },
    flexShrink: '0',

    // THE MODE CHIP — the bar's LEFT edge names what the AI makes
    // (AI_ASSISTANT_SPEC §8 "left: mode switch (Assistant · Dashboard ·
    // Apps)", Nika's pick 5B): the kit's AssistantModeChip (shared) opens the
    // mode menu and writes through the shell's setAssistantMode. FIRST here,
    // in the component: a key the shell mount adds renders AFTER this row's
    // own children, and a flex `order` would leave the Tab order behind the
    // pixels. It hides itself without the writer (another host has none) and
    // in the small pill — no gate here, which would replace its own. Out of
    // the row's NavbarButton like the shell's ModelBtn, and the row's
    // childProps padding (Y) outranks the kit's, so its geometry is re-stated.
    // The key names the kit component, so it extends it by key (FA806).
    // RIGID: the kit chip never shrinks (e0fe2a39d) and nothing here overrides
    // that — the mode word is never cut. The row fits every language at every
    // width since the compact model button (789772f71, b37eac652; measured
    // budget in __tests__/composerToolRowGeometry.test.js).
    AssistantModeChip: {
      ignoreChildExtends: true,
      // the merged form: context + mode in this one trigger (see `Prefix`)
      withContext: true,
      // the home's tabs are the mode there (see `_modeChipHidden`)
      hide: (el, s) => _modeChipHidden(el, s),
      padding: 'Y Z2',
      // the small pill carries its own chip FIRST (the brand `Prefix` slot)
      class: {
        isRowInPill: (el, s) => s.size === 'small',
        isCompact: (el, s) => s.size === 'small'
      },
      '&.isRowInPill': { display: 'none' }
    },

    // Content-attach entry (2026-09-02 per Nika "add + icon where you
    // select different content - like ticket, member, note etc ... but also
    // add option in + dropdown (build app)"). Toggles the AttachMenu panel
    // (component-local state — works without the dropdown plugin on the
    // standalone canvas host).
    PlusBtn: {
      // Armed build mode is carried by the GLYPH, not a wash: conditional
      // classes never attach on these childExtends row buttons (measured
      // live — the node carried no isActive class while armed, across
      // class/factory/subscribeTo variants), but CONTENT props re-apply
      // reliably (VoiceBtn's mic swap is the precedent). While armed the +
      // becomes the build glyph, the tooltip says how to disarm, and the
      // textarea placeholder names what the next send does.
      icon: (el, s) => (s.buildArmed ? 'layers' : 'plus'),
      aria: {
        label: (el) => el.call('polyglot', 'canvas.prompt.attach.aria'),
        haspopup: 'menu',
        expanded: (el, s) => (s.attachOpen ? 'true' : 'false')
      },
      title: (el, s) =>
        el.call(
          'polyglot',
          s.buildArmed ? 'canvas.prompt.attach.armedTitle' : 'canvas.prompt.attach.aria'
        ),
      // ArrowDown/ArrowUp open the menu (the menu-button contract) and hand
      // focus to its first/last row. ArrowUp landing on the LAST row is the
      // menu-button convention. Enter and Space need no branch — NavbarButton
      // is a real <button>, so both already fire the onClick toggle below.
      //
      // The arm happens HERE, not on the panel: see the _armAttachFocus note
      // at the top of this file for why an `if:`-gated panel gets no open-edge
      // lifecycle hook of its own. Arming on an already-open menu is correct
      // too — the arrow keys must reach the rows whichever way focus was lost.
      onKeydown: (e, el, s) => {
        if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return
        e.preventDefault()
        e.stopPropagation()
        if (!s.attachOpen) _attachUpdate(s, { attachOpen: true })
        _armAttachFocus(el, { last: e.key === 'ArrowUp' })
      },
      onClick: (e, el, s) => {
        e.preventDefault()
        e.stopPropagation()
        const next = !s.attachOpen
        _attachUpdate(s, { attachOpen: next })
        // Opening by pointer moves focus into the menu too (the ARIA
        // menu-button pattern) so the arrows work without a second click.
        // Closing by pointer drops the stored return target instead of
        // restoring it — focus is already on this button.
        if (next) _armAttachFocus(el)
        else _releaseMenuFocus(_attachPanelOf(el))
      }
    },

    InspectBtn: {
      icon: 'sf cursor',
      title: (el) => el.call('polyglot', 'canvas.prompt.inspect'),
      onClick: (ev, el) => {
        el.call('setCanvasMode', 'inspectMode')
      }
    },
    // Voice input — click toggles the full-screen listening overlay, holding
    // (≥280ms) is true push-to-talk: speak while held, release to execute.
    // Renders only when a VoiceOverlay host announced itself (voiceHostReady).
    // `display: null` clears NavbarButton's inline display so the reactive
    // `show` can win (the UploadBtn pattern below). Own `scope` so the PTT
    // hold timer never collides with the inherited composer scope.
    VoiceBtn: {
      scope: {},
      display: null,
      // `s.root &&` short-circuits, so on any run where `s.root` is not yet
      // populated this factory finishes with `voiceHostReady` UNTRACKED and
      // (per the zero-tracked-sources rule) is never notified again — the mic
      // button then stays hidden for the rest of the session even after a
      // VoiceOverlay host announces itself. Declaring the dep makes the
      // subscription independent of control flow. FA515.
      showDeps: ['voiceHostReady'],
      show: (el, s) => !!(s.root && s.root.voiceHostReady),
      icon: (el, s) => (s.root && s.root.voiceOpen ? 'mic' : 'micOutline'),
      // VOICE-TALK-ONLY-SIMONE-1 part 2: a plain CLICK opens TALK (the
      // conversation — voiceStart's default now); HOLDING keeps the one-shot
      // spoken-command pipeline (explicit mode below on the press only).
      title: (el) => el.call('polyglot', 'canvas.prompt.voice'),
      isActive: (el, s) => !!(s.root && s.root.voiceOpen),
      '.isActive': { background: 'selected', color: 'title' },
      onPointerdown: (e, el, s) => {
        if (e.button !== 0) return
        // Capture on the BUTTON, not `e.target`. e.target is whatever sits
        // under the pointer — normally the inner icon — and `icon` is a
        // reactive prop that swaps the instant `voiceOpen` flips, i.e.
        // exactly when a PTT hold starts. Capture dies with the node it was
        // taken on, so capturing the icon could drop `pointerup` mid-hold,
        // leaving `_vpttHeld` true and making the NEXT click commit-and-close
        // instead of toggling. The button itself survives the icon swap (and
        // survives `PromptButtons` hiding while a strip hosts — display:none
        // does not remove it from the document, which is what releases capture).
        const capture = el.node || e.target
        if (capture && capture.setPointerCapture && e.pointerId !== undefined) {
          try {
            capture.setPointerCapture(e.pointerId)
          } catch (_) {}
        }
        // Announce this composer as the voice host BEFORE the engine opens —
        // root.voiceHost contract (shared/components/VoiceStrip.js): the
        // in-place strip renders here instead of the full-screen overlay.
        // UI-only root write → preventFetch.
        if (s.root && s.root.update) {
          s.root.update({ voiceHost: 'canvas' }, { preventFetch: true })
        }
        el.call('voicePttPress', { mode: 'command' })
      },
      onPointerup: (e, el) => {
        if (e.button !== 0) return
        // No mode on the release path: a quick click falls through to
        // voiceToggle → voiceStart, whose default is TALK. The press keeps
        // `mode: 'command'` so the ≥280ms HOLD (one-shot command) is the
        // only way to reach the one-shot pipeline.
        el.call('voicePttRelease')
      },
      onPointercancel: (e, el) => el.call('voicePttCancel')
    },
    UploadBtn: {
      // `extends: 'UploadThreadImageIcon'` pointed at a non-existent
      // component → the button rendered with no icon and the wrong
      // box-model. NavbarButton (inherited via PromptButtons.childExtends)
      // already gives us the toolbar style; we just add an upload icon
      // and a hidden file input layered on top of the button surface.
      tag: 'label',
      icon: 'uploadOutline',
      title: (el) => el.call('polyglot', 'canvas.prompt.uploadImage'),
      // ON THE ROW ONLY IN THE WIDE `large` COMPOSER. With the mode chip the
      // resting composer (width H) is at capacity — measured on dev
      // 3691a9c28 at 1440: the tools need 349 px of 347 in English, ~373 in
      // Georgian — and a phone has less; there Upload lives in the + menu,
      // whose "Photo or file" row clicks THIS button's file input (it stays in
      // the DOM, display:none — MDN's hidden-input pattern). Hidden in the
      // small pill as before. `display: null` clears the NavbarButton inline
      // `display` so the reactive `hide` can win (the InspectBtn pattern).
      display: null,
      hide: (el, s) => s.size !== 'large',
      '@mobileM': { display: 'none' },
      position: 'relative',
      cursor: 'pointer',
      Input: {
        tag: 'input',
        type: 'file',
        ariaLabel: (el) => el.call('polyglot', 'canvas.prompt.uploadImage'),
        accept: 'image/*',
        position: 'absolute',
        inset: '0',
        opacity: '0',
        cursor: 'pointer',
        onChange: (e, el) => {
          const file = e.target.files[0]
          if (!file || !file.type.startsWith('image/')) return
          // Walk up to the CanvasPromptTextarea element to reach its component
          // state (state.images). The FileReader callback fires after an async
          // hop, so we capture both `st` (component state) and `el.node` refs
          // synchronously before yielding.
          let promptEl = el.parent // Input → UploadBtn
          while (promptEl && promptEl.parent && !promptEl.state?.images) {
            promptEl = promptEl.parent
          }
          const st = promptEl?.state
          if (!st) return
          const nodeRef = el.node
          const reader = new FileReader()
          reader.onloadend = () => {
            // Capture name/mime/size alongside the data URL — same shape as
            // the composer's onDrop/onPaste image capture (see onDrop above).
            const key = `${file.name}:${file.size}:${file.lastModified}`
            const existing = st.images || []
            if (st.update && !existing.some((ex) => ex.key === key)) {
              st.update({
                images: [
                  ...existing,
                  {
                    key,
                    name: file.name,
                    mime: file.type || 'image/*',
                    size: file.size,
                    dataUrl: reader.result
                  }
                ]
              })
            }
            if (nodeRef) nodeRef.value = ''
          }
          reader.readAsDataURL(file)
        }
      }
    },
    ExpandBtn: {
      icon: 'arrowAngleUp',
      // Icon-only control — same audit class as 59a72b64: without a label
      // it announces NOTHING to screen readers.
      aria: { label: (el) => el.call('polyglot', 'canvas.prompt.expandBar') },
      title: (el) => el.call('polyglot', 'canvas.prompt.expandBar'),
      show: (el, s) => s.size === 'small',
      // PHONES: Expand leaves the pill row for the + menu (design-d2's ruling
      // 2026-10-01: the 390 pill had 136 px of text room beside five
      // controls; Voice stays). The SAME narrow-row rule as UploadBtn
      // (`@mobileM`); the + menu's ExpandRow appears exactly when this button
      // is hidden by it (it reads this node's computed display). `display:
      // null` clears the NavbarButton inline display so the media rule wins.
      display: null,
      '@mobileM': { display: 'none' },
      onClick: (e, el, s) => {
        // Clear the small-state autosize height BEFORE it can fight
        // 'normal's declarative sizing — see _resetTextareaAutosize's own
        // comment for the full stale-inline-height race — then settle
        // again AFTER the new cascade applies (_settleTextareaSize: the
        // update itself re-arms a stale height via the autoGrow resize).
        _resetTextareaAutosize(el)
        s.update({ size: 'normal' })
        _settleTextareaSize(el)
      }
    }
  },

  Flex: null,
  Button: null,

  // ── Mention typeahead dropdown ──────────────────────────────────────────
  // Anchored above the composer (the SimoneMentionDropdown posture —
  // absolute, bottom:100%), driven off the component-local `mentionPicker`
  // state _refreshAiMention maintains. Rows are per-option child STATES; a
  // row's `s.parent` is the composer state, which is how a click reaches
  // the picker's splice coordinates.
  AiMentionDropdown: {
    if: (el, s) => !!(s.mentionPicker && s.mentionPicker.options && s.mentionPicker.options.length),
    position: 'absolute',
    bottom: '100%',
    left: 'A',
    marginBottom: 'Y',
    zIndex: '40',
    flow: 'y',
    gap: 'V',
    padding: 'Y',
    minWidth: 'G',
    maxWidth: 'H',
    round: 'radiusCard',
    theme: 'common-card',
    boxShadow: 'var(--shadow-float)',
    overflow: 'hidden',
    // A typeahead list, NOT a menu: DOM focus stays in the textarea (moving
    // it would drop the caret and the query the user is still typing), and
    // the highlight travels as `mentionPicker.activeIndex` state. So the
    // roles are listbox/option — the keys themselves (Arrow/Enter/Tab/Escape,
    // plus Home/End as of this pass) already live on the textarea's own
    // onKeydown above and are deliberately NOT rebuilt here.
    role: 'listbox',
    aria: { label: (el) => el.call('polyglot', 'canvas.prompt.mentionSuggestions') },
    // Section header — the SAME CaptionTitle voice the sidebars head their
    // groups with (chat Sidebar.js: `CaptionTitle` at Y2 over CHANNELS /
    // DIRECT MESSAGES), so a dropdown list and a rail list read as one
    // grammar (2026-09-02 per Nika "in both AI dropdowns - use CaptionTitle
    // just like we use in sidebars - with same color and size"). Names the
    // trigger's type (People / Channels / Tickets / Events / Notes & docs).
    // Function-form text: this element has no state of its own, so `s` IS
    // the composer state and re-runs on every picker update.
    Heading: {
      extends: 'CaptionTitle',
      // X2, not the sidebar's literal Y2: the type scale is em-based and the
      // chat rail runs at base Z (17px / 1.2 = 14.17px), where Y2 renders
      // 11.05px; this dropdown runs at the composer's 17px base, where Y2
      // renders 13.26px. X2 = Y2 / 1.2, so X2 here IS the sidebar's 11.05px
      // (measured live 2026-09-02 — "same color and size" is a rendered
      // contract, not a token name).
      fontSize: 'X2',
      margin: '0',
      padding: 'Y Z',
      align: 'center flex-start',
      role: 'presentation',
      Text: {
        // Literal keys, never `'canvas.prompt.mention.' + type` — the canvas
        // i18n audit (canvasI18n test) refuses concatenated keys because it
        // cannot prove they exist in both bundles.
        text: (el, s) => {
          const type = (s.mentionPicker && s.mentionPicker.type) || 'person'
          const key =
            {
              person: 'canvas.prompt.mention.person',
              channel: 'canvas.prompt.mention.channel',
              ticket: 'canvas.prompt.mention.ticket',
              event: 'canvas.prompt.mention.event',
              reference: 'canvas.prompt.mention.reference'
            }[type] || 'canvas.prompt.mention.person'
          return el.call('polyglot', key)
        }
      }
    },
    // The option rows live one level down so the header can sit beside a
    // `children` list without inheriting its childExtends. `Rows` declares
    // no state, so it INHERITS the composer's state object (smbls
    // state/create.js "No state defined - inherit parent's state"): each
    // row's `s.parent` is still the composer state, exactly what
    // _applyAiMention and the activeIndex readers below expect.
    Rows: {
      flow: 'y',
      gap: 'V',
      role: 'presentation',
      childrenAs: 'state',
      children: (el, s) =>
        ((s.mentionPicker && s.mentionPicker.options) || []).map((o, i) => ({
          ...o,
          __i: i,
          __type: s.mentionPicker.type,
          key: String(o.id || o.email || o.name || o.title || i)
        })),
      childExtends: {
        flow: 'x',
        align: 'center space-between',
        gap: 'Z',
        padding: 'Y Z',
        round: 'radiusControl',
        cursor: 'pointer',
        role: 'option',
        ariaSelected: (el, s) =>
          s.parent && s.parent.mentionPicker && (s.parent.mentionPicker.activeIndex || 0) === s.__i
            ? 'true'
            : 'false',
        background: (el, s) =>
          s.parent && s.parent.mentionPicker && (s.parent.mentionPicker.activeIndex || 0) === s.__i
            ? 'selected'
            : 'transparent',
        ':hover': { background: 'hover' },
        Label: {
          tag: 'span',
          fontSize: 'Z1',
          color: 'title',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          text: (el, s) => _mentionRefRowLabel(s.__type, s).label
        },
        Caption: {
          tag: 'span',
          fontSize: 'Y2',
          color: 'caption',
          flexShrink: '0',
          text: (el, s) => _mentionRefRowLabel(s.__type, s).caption
        },
        // preventDefault on pointerdown keeps focus in the textarea so the
        // blur-close belt never races the pick.
        onPointerdown: (e) => e.preventDefault(),
        onClick: (e, el, s) => {
          e.preventDefault()
          e.stopPropagation()
          if (s.parent) _applyAiMention(el, s.parent, s)
        }
      }
    }
  },

  // ── '+' attach menu ─────────────────────────────────────────────────────
  // Same posture as the mention dropdown; static row set. Content rows
  // insert their trigger prefix at the end of the draft and hand off to the
  // typeahead; Build app forces the /add-app build flow (shell-registered
  // forceBuildApp — the row hides where no host registers it, e.g. the
  // standalone canvas).
  AttachMenu: {
    if: (el, s) => !!s.attachOpen,
    position: 'absolute',
    bottom: '100%',
    left: 'A',
    marginBottom: 'Y',
    zIndex: '40',
    flow: 'y',
    gap: 'V',
    padding: 'Y',
    minWidth: 'F',
    round: 'radiusCard',
    theme: 'common-card',
    boxShadow: 'var(--shadow-float)',
    // ── keyboard (2026-09-02 per Nika "add keyboard navigation when ai
    // chatbot dropdowns open") ─────────────────────────────────────────────
    // Before this the whole menu was click-only: it could not be opened from
    // the keyboard, arrowed through, or dismissed.
    //
    // The rows stay <div>s with `role="menuitem"` + `tabindex="-1"` (the ARIA
    // menu pattern) rather than becoming <button>s. Two reasons, both
    // measured on this file: a <button> does not inherit the app font, so
    // every row's Label/Caption would have fallen back to the UA default; and
    // roving `tabindex="-1"` is CORRECT here (unlike the AI settings panel) —
    // this is a transient menu whose only entry point is the '+' button, so
    // its rows must not mint six extra tab stops in the composer toolbar.
    // Because the rows are not buttons, Enter/Space are activated explicitly
    // by `menuActivateRow`, which clicks the focused row (a real bubbling
    // click — exactly what DOMQL's delegated onClick listens for).
    //
    // `if:`-gated, so the node exists ONLY while open. That keeps six rows out
    // of the DOM while the menu is shut, but it also means this panel gets NO
    // lifecycle hook on either edge — the focus arm/return lives at the call
    // sites that flip `attachOpen` (see the _armAttachFocus note at the top of
    // this file), and the close branches below own the return leg.
    role: 'menu',
    tabindex: '-1',
    aria: { label: (el) => el.call('polyglot', 'canvas.prompt.attach.aria') },
    onKeydown: (e, el, s) => {
      if (_menuArrowNav(e, el.node)) return
      if (_menuActivateRow(e, el.node)) return
      if (e.key === 'Escape') {
        e.preventDefault()
        e.stopPropagation()
        _attachUpdate(s, { attachOpen: false })
        // Escape hands focus back to '+' — the ARIA menu contract, and the
        // only way a keyboard user gets back to the toolbar. `el` IS the panel
        // here, which is where the arm stored the return target.
        _restoreMenuFocus(el)
        return
      }
      // Tab leaves the menu — close on the way out, without preventDefault so
      // the browser still moves focus onward. The return target is dropped
      // first: the close path would otherwise pull focus back to '+' and
      // fight the Tab the user just pressed.
      if (e.key === 'Tab') {
        _releaseMenuFocus(el)
        _attachUpdate(s, { attachOpen: false })
      }
    },
    scope: {
      insertTrigger(el, s, prefix) {
        const body = s.draft || ''
        const sep = body && !/\s$/.test(body) ? ' ' : ''
        const next = body + sep + prefix
        // This path OWNS where focus goes (the textarea, caret at the end),
        // so the menu's stored return target is dropped and any arm poll
        // still in flight is cancelled — otherwise a fast keyboard pick would
        // race the 300ms handoff below and park focus back on '+'.
        _releaseMenuFocus(_attachPanelOf(el))
        _attachUpdate(s, { draft: next, attachOpen: false })
        // 300ms, not a rAF: the menu-close re-render repaints the textarea
        // value a beat later and the Textarea's onBlur belt (250ms) clears
        // a picker opened before it settles — measured live: '@' landed but
        // focus and the picker were both gone by +800ms on a rAF handoff.
        setTimeout(() => {
          const ta = el.node
            ?.closest?.('[data-key="CanvasPromptTextarea"]')
            ?.querySelector?.('textarea')
          if (!ta) return
          ta.focus()
          try {
            ta.setSelectionRange(next.length, next.length)
          } catch (_) {}
          const taEl = { node: ta, state: el.state }
          _refreshAiMention(taEl, s)
        }, 300)
      }
    },
    childExtends: {
      flow: 'x',
      align: 'center flex-start',
      gap: 'Z',
      padding: 'Y Z',
      round: 'radiusControl',
      cursor: 'pointer',
      background: 'transparent',
      // Keyboard row contract (see the panel note above): `menuitem` is what
      // the arrow walker collects, `-1` keeps the rows out of the tab order
      // while still allowing programmatic focus. Focus is visible through the
      // same `hover` wash the pointer gets, so a keyboarded row is never a
      // silent selection.
      role: 'menuitem',
      tabindex: '-1',
      ':hover': { background: 'hover' },
      ':focus-visible': { background: 'hover', outline: 'none' },
      onPointerdown: (e) => e.preventDefault()
    },
    // Section header — same CaptionTitle voice as the sidebars' group
    // titles and the mention dropdown above (2026-09-02 per Nika). It
    // inherits the row childExtends (menuitem role, hover wash, pointer),
    // so it opts back out: `presentation` keeps it out of the arrow walker
    // (which collects `[role="menuitem"]`), and the washes are cleared.
    Heading: {
      extends: 'CaptionTitle',
      // X2 = the sidebar's rendered Y2 at this 17px base — see the mention
      // dropdown's Heading above.
      fontSize: 'X2',
      margin: '0',
      padding: 'Y Z',
      align: 'center flex-start',
      role: 'presentation',
      tabindex: null,
      cursor: 'default',
      ':hover': { background: 'transparent' },
      ':focus-visible': { background: 'transparent' },
      Text: { text: '{{ canvas.prompt.attach.heading | polyglot }}' }
    },
    // FLY'S ADD SECTION (Nika 2026-10-23: "I'm not sure about these buttons …
    // maybe we move them somewhere else?"): the Fly canvas carries no toolbar;
    // while it is on screen, the "+" adds a live widget to its page — the
    // shell's section picker (workspace functions/assistantFlyWorkspace.js
    // assistantFlySectionOffered / assistantFlyWorkspacePicker). A host without
    // Fly registers neither, so the row is never there.
    FlySectionRow: {
      if: (el, s) =>
        typeof el.context?.functions?.assistantFlySectionOffered === 'function' &&
        !!el.call('assistantFlySectionOffered', s.root),
      Icon: { name: 'columns', boxSize: 'Z', color: 'title', flexShrink: '0' },
      Label: {
        tag: 'span',
        fontSize: 'Z1',
        color: 'title',
        text: '{{ canvas.prompt.attach.section | polyglot }}'
      },
      onClick: (e, el, s) => {
        e.preventDefault()
        e.stopPropagation()
        // The picker's dialog takes focus next and returns it to whatever had
        // it when it opened: focus goes back to '+' first (Escape's path), so
        // '+' is where the closed dialog lands — this row leaves with the menu.
        _restoreMenuFocus(_attachPanelOf(el))
        el.call('assistantFlyWorkspacePicker', 'section')
        _attachUpdate(s, { attachOpen: false })
      }
    },
    // TOGGLE, not a navigation (2026-09-02 per Nika "buld app should not
    // open /add-app section but should be toggle which runs this
    // functionality and opens that generated page after output"): arming
    // flips root._aiBuildMode; the NEXT sends run the build pipeline from
    // right here, and the generated page opens as a PageSlot overlay when
    // the output lands (functions/forceBuildApp.js + watchAiBuildOutput).
    BuildAppRow: {
      if: (el) => typeof el.context?.functions?.forceBuildApp === 'function',
      background: (el, s) => (s.root && s.root._aiBuildMode ? 'selected' : 'transparent'),
      Icon: { name: 'layers', boxSize: 'Z', color: 'title', flexShrink: '0' },
      Label: {
        tag: 'span',
        fontSize: 'Z1',
        color: 'title',
        text: '{{ canvas.prompt.attach.buildApp | polyglot }}'
      },
      Caption: {
        tag: 'span',
        fontSize: 'Y2',
        color: 'caption',
        text: (el, s) =>
          el.call(
            'polyglot',
            s.root && s.root._aiBuildMode
              ? 'canvas.prompt.attach.buildAppArmed'
              : 'canvas.prompt.attach.buildAppHint'
          )
      },
      onClick: (e, el, s) => {
        e.preventDefault()
        e.stopPropagation()
        const next = !(s.root && s.root._aiBuildMode)
        // Nothing else takes focus on this path — the menu just closes — so
        // hand it back to '+' the way Escape does. Without this a keyboard
        // activation (Enter on the focused row) drops focus to <body>.
        _restoreMenuFocus(_attachPanelOf(el))
        // buildArmed mirrors the root flag for the PlusBtn paint (see its
        // isActive note); the root flag stays the behavioural truth.
        _attachUpdate(s, { attachOpen: false, buildArmed: next })
        if (s.root && s.root.update) s.root.update({ _aiBuildMode: next }, { preventFetch: true })
      }
    },
    UploadRow: {
      Icon: { name: 'uploadOutline', boxSize: 'Z', color: 'title', flexShrink: '0' },
      Label: {
        tag: 'span',
        fontSize: 'Z1',
        color: 'title',
        text: '{{ canvas.prompt.attach.photoOrFile | polyglot }}'
      },
      onClick: (e, el, s) => {
        e.preventDefault()
        e.stopPropagation()
        // The OS file picker takes focus next — drop the return target so the
        // menu's close path never pulls it back to '+' behind the dialog.
        _releaseMenuFocus(_attachPanelOf(el))
        _attachUpdate(s, { attachOpen: false })
        const input = el.node
          ?.closest?.('[data-key="CanvasPromptTextarea"]')
          ?.querySelector?.('input[type="file"]')
        if (input) input.click()
      }
    },
    // EXPAND on phones (see PromptButtons.ExpandBtn): offered only while the
    // pill's own Expand button is hidden by the narrow-row rule — read off
    // that button's computed display when the menu opens (the menu mounts
    // on open), so the row and the button are never both shown or both gone.
    ExpandRow: {
      if: (el, s) => {
        if (s.size !== 'small') return false
        const row = el.parent && el.parent.parent && el.parent.parent.PromptButtons
        const node = row && row.ExpandBtn && row.ExpandBtn.node
        if (!node || typeof getComputedStyle !== 'function') return false
        return getComputedStyle(node).display === 'none'
      },
      Icon: { name: 'arrowAngleUp', boxSize: 'Z', color: 'title', flexShrink: '0' },
      Label: {
        tag: 'span',
        fontSize: 'Z1',
        color: 'title',
        text: '{{ canvas.prompt.expandBar | polyglot }}'
      },
      onClick: (e, el, s) => {
        e.preventDefault()
        e.stopPropagation()
        _attachUpdate(s, { attachOpen: false })
        // the Expand button's own path: the autosize reset, the size, the settle
        _resetTextareaAutosize(el)
        s.update({ size: 'normal' })
        _settleTextareaSize(el)
      }
    },
    TicketRow: {
      Icon: { name: 'checkOval', boxSize: 'Z', color: 'title', flexShrink: '0' },
      Label: {
        tag: 'span',
        fontSize: 'Z1',
        color: 'title',
        text: '{{ canvas.prompt.attach.ticket | polyglot }}'
      },
      Caption: { tag: 'span', fontSize: 'Y2', color: 'caption', text: '##' },
      onClick: (e, el, s) => {
        e.preventDefault()
        e.stopPropagation()
        el.scope.insertTrigger(el, s, '##')
      }
    },
    MemberRow: {
      Icon: { name: 'user', boxSize: 'Z', color: 'title', flexShrink: '0' },
      Label: {
        tag: 'span',
        fontSize: 'Z1',
        color: 'title',
        text: '{{ canvas.prompt.attach.member | polyglot }}'
      },
      Caption: { tag: 'span', fontSize: 'Y2', color: 'caption', text: '@' },
      onClick: (e, el, s) => {
        e.preventDefault()
        e.stopPropagation()
        el.scope.insertTrigger(el, s, '@')
      }
    },
    EventRow: {
      Icon: { name: 'dateOutline', boxSize: 'Z', color: 'title', flexShrink: '0' },
      Label: {
        tag: 'span',
        fontSize: 'Z1',
        color: 'title',
        text: '{{ canvas.prompt.attach.event | polyglot }}'
      },
      Caption: { tag: 'span', fontSize: 'Y2', color: 'caption', text: '@@' },
      onClick: (e, el, s) => {
        e.preventDefault()
        e.stopPropagation()
        el.scope.insertTrigger(el, s, '@@')
      }
    },
    NoteRow: {
      Icon: { name: 'document', boxSize: 'Z', color: 'title', flexShrink: '0' },
      Label: {
        tag: 'span',
        fontSize: 'Z1',
        color: 'title',
        text: '{{ canvas.prompt.attach.note | polyglot }}'
      },
      Caption: { tag: 'span', fontSize: 'Y2', color: 'caption', text: '+' },
      onClick: (e, el, s) => {
        e.preventDefault()
        e.stopPropagation()
        el.scope.insertTrigger(el, s, '+')
      }
    }
  },

  ExpandCornerBtn: {
    extends: 'NavbarButton',
    if: (el, s) => s.size !== 'small' && !_voiceHosting(s),
    // Arrows point INWARD when large (fullscreenExit = collapse) and
    // OUTWARD when normal (fullscreen = expand) — 2026-06-08 per Nika
    // "arrows should point inwards when it's large".
    icon: (el, s) => (s.size === 'large' ? 'fullscreenExit' : 'fullscreen'),
    aria: {
      label: (el, s) =>
        el.call(
          'polyglot',
          s.size === 'large' ? 'canvas.prompt.collapseComposer' : 'canvas.prompt.expandComposer'
        )
    },
    title: (el, s) =>
      el.call(
        'polyglot',
        s.size === 'large' ? 'canvas.prompt.collapseComposer' : 'canvas.prompt.expandComposer'
      ),
    position: 'absolute',
    top: 'Z',
    right: 'Z',
    padding: 'Y2',
    fontSize: 'Z1',
    opacity: '.5',
    ':hover': { opacity: '1' },
    onClick: (e, el, s) => {
      // Same stale-inline-height race as ExpandBtn — see
      // _resetTextareaAutosize's own comment; post-update settle per
      // _settleTextareaSize.
      _resetTextareaAutosize(el)
      if (s.size === 'normal') s.update({ size: 'large' })
      else s.update({ size: 'normal' })
      _settleTextareaSize(el)
    }
  },

  // Open the same conversation in the assistant sidebar (2026-09-02 per
  // Nika "add open in sidebar icon in canvasPromptTextarea when its
  // expanded... near fullscreen icon"). Sits in the top-right corner
  // cluster beside ExpandCornerBtn (`right: 'D'` — the same second-corner
  // offset MinimizeBtn uses on the bottom edge). Opens the drawer with the
  // canonical ReasoningPill/AppAssistantToggle patch and collapses the
  // composer back to the resting pill — the sidebar is now the working
  // surface; the draft stays in the composer state untouched.
  OpenSidebarBtn: {
    extends: 'NavbarButton',
    if: (el, s) => s.size !== 'small' && !_voiceHosting(s),
    icon: 'sidebarRightFill',
    aria: { label: (el) => el.call('polyglot', 'canvas.prompt.openInSidebar') },
    title: (el) => el.call('polyglot', 'canvas.prompt.openInSidebar'),
    position: 'absolute',
    top: 'Z',
    right: 'D',
    padding: 'Y2',
    fontSize: 'Z1',
    // Muted-ink rest state + full-ink hover (the Chat.js session-menu
    // recipe) — NOT the neighbours' opacity fade, which the design-language
    // lock retired for new code (opacity-hover).
    color: 'caption',
    ':hover': { color: 'title' },
    onClick: (e, el, s) => {
      // The shell's ONE "Open in sidebar" (workspace functions/
      // assistantMainThread.js assistantThreadToSidebar): in Page mode an
      // OPENER opens the main thread (AI_ASSISTANT_SPEC §12.2), but this button
      // names the SIDEBAR — the drawer on the last side for the session, the
      // thread closed, closing the drawer returns to Page. A host without the
      // shell's writer (the standalone canvas) opens the drawer as before.
      const fns = el.context && el.context.functions
      if (fns && typeof fns.assistantThreadToSidebar === 'function') {
        el.call('assistantThreadToSidebar')
        if (s.root._assistantPillHandle) s.root.update({ _assistantPillHandle: false })
      } else {
        const patch = { assistantOpen: true }
        if (s.root._assistantPillHandle) patch._assistantPillHandle = false
        if (s.root.assistantDock === 'minimized') patch.assistantDock = 'right'
        s.root.update(patch)
      }
      _resetTextareaAutosize(el)
      s.update({ size: 'small' })
      _settleTextareaSize(el)
    }
  },

  MinimizeBtn: {
    extends: 'NavbarButton',
    if: (el, s) => s.size !== 'small' && !_voiceHosting(s),
    icon: 'arrowAngleDown',
    aria: { label: (el) => el.call('polyglot', 'canvas.prompt.minimizeBar') },
    title: (el) => el.call('polyglot', 'canvas.prompt.minimizeBar'),
    position: 'absolute',
    bottom: 'Z',
    right: 'D',
    // The icon-button square (the kit's IconButton law), named here because
    // the docked tool row reserves exactly this box: `right: calc(D +
    // controlHCompact + X)` on PromptButtons.
    boxSize: 'controlHCompact',
    padding: 'Y2',
    fontSize: 'Z1',
    onClick: (e, el, s) => {
      // Same stale-inline-height race as ExpandBtn/ExpandCornerBtn — see
      // _resetTextareaAutosize's own comment. The post-update settle is
      // what actually fixes the "minimize keeps the expanded size" bug:
      // the update's own autoGrow resize re-pins a height measured under
      // the still-expanded cascade, so it must be cleared and re-measured
      // AFTER the small cascade applies (_settleTextareaSize).
      _resetTextareaAutosize(el)
      s.update({ size: 'small' })
      _settleTextareaSize(el)
    }
  },

  CircleButton: {
    // The house icon button (36px, 1/1 — the brand's icon-button law), the
    // same square as the row's buttons beside it. It was a bare CircleButton:
    // a 30px disc in a row of 36px buttons (measured live 2026-10-18).
    extends: 'NavbarButton',
    // Hidden while the voice strip hosts (T18) — sends come from the voice
    // flow, not the text send button.
    hide: (el, s) => _voiceHosting(s),
    // Return-to-typing entrance, symmetric with Textarea's — see its
    // comment above.
    animation: 'fadeIn',
    // Icon-only send control — carried NO accessible name at all: a <button>
    // whose only content is an svg glyph announces NOTHING to screen readers,
    // and was the last remaining CRITICAL axe `button-name` node on every
    // route this composer mounts on (2026-08-08 marketplace sweep,
    // tickets/fable.md MARKETPLACE-AUDIT). Same aria+title pair as
    // ExpandBtn/MinimizeBtn/ExpandCornerBtn above — static aria.X flattens
    // to a real `aria-label` attribute (attrs-in-props
    // filterAttributesByTagName).
    aria: { label: (el) => el.call('polyglot', 'canvas.prompt.send') },
    title: (el) => el.call('polyglot', 'canvas.prompt.send'),
    position: (el, s) => (s.size === 'small' ? 'relative' : 'absolute'),
    bottom: (el, s) => (s.size === 'small' ? 'auto' : 'Z'),
    right: (el, s) => (s.size === 'small' ? 'auto' : 'Z'),
    // CONCENTRIC IN THE PILL'S RIGHT CAP. The send is the 36 px house icon
    // button now (NavbarButton above), so on the 39 px mouse pill `V` (1.53 px)
    // leaves the same 1.5 px on its right as above and below it (as a 30 px
    // disc it sat 2 px from the edge with 4.5 px above and below). On a touch
    // screen the pill stands 45 px: `X1` (4.66 px) sits the circle concentric
    // in the cap, the way the mode plate sits in the left one (measured at
    // 390, 2026-10-18). A WRAPPED pill is a box, not a cap: it anchors its
    // controls `X` above its bottom edge (.isPillWrapped), and the send keeps
    // the same `X` from the end — the corner the chip keeps at the start (it
    // kept `V`: 1.5 px from the right, 4 px from the bottom, live on dev
    // 7227774). The pill's own verdict: smbls keeps a case's EVALUATED value
    // on the element (a boolean at runtime — measured on dev 3103cfd, where a
    // function check missed it; the definition's function in a unit test).
    // The draft read subscribes this factory to the edits that flip it, and
    // the pill's case re-runs first (effects run parent before child).
    marginRight: (el, s) => {
      if (s.size !== 'small') return undefined
      if (_touchScreen(el)) return 'X1'
      void s.draft
      const pill = el && el.parent
      const verdict = pill ? pill.isPillWrapped : false
      const wrapped =
        typeof verdict === 'function'
          ? !!(typeof el.call === 'function' && el.call('exec', verdict, pill))
          : !!verdict
      return wrapped ? 'X' : 'V'
    },
    flexShrink: '0',
    // The send arrow in every state — the drawer's arrow (Chat.js SendButton),
    // and no longer the paper plane the Fly mode chip paints. A send in flight
    // swaps it for the house spinner: the sprite has no `loading` glyph, so
    // that name painted `#noIcon`, a hatched square.
    icon: 'arrowUp',
    Icon: {
      hideDeps: [(el, s) => s.root && s.root.freestyleBusy],
      hide: (el, s) => !!(s && s.root && s.root.freestyleBusy)
    },
    LoadingSpinner: {
      hideDeps: [(el, s) => s.root && s.root.freestyleBusy],
      hide: (el, s) => !(s && s.root && s.root.freestyleBusy)
    },
    opacity: (el, s) => (s.root?.freestyleBusy ? '.5' : '1'),
    pointerEvents: (el, s) => (s.root?.freestyleBusy ? 'none' : 'auto'),
    padding: (el, s) => (s.size === 'small' ? 'Y' : 'Z'),
    theme: 'canvas-card-glass-reversed',
    // The same thumb target as the row's buttons (30px measured). Border-box:
    // this is a content-box button, so a bare 36 px min-width floor added the
    // padding on top — a 56 px send that reached into minimize (dev
    // 3691a9c28, 390: send [312, 368] against minimize [280, 316]).
    '@mobileL': {
      minWidth: 'controlHCompact',
      minHeight: 'controlHCompact',
      boxSizing: 'border-box'
    },
    // A finger's target — the 44pt FLOOR (shared/components/_touchTarget.js).
    // Send is the composer's most-used control and measured 25 x 25 on an
    // 834px iPad, 28 x 28 at 1032+ and 36 x 36 on phones (dev DFI, touch
    // simulated, DESIGN-SHELL-RESPONSIVE-IOS-SAFARI-1). The button is already
    // positioned (relative / absolute by size), so no position here.
    '@coarse': { ...touchFloor },
    onClick: (e, el) => {
      _sendPrompt(el)
    }
  }
}
